
        <template>
          <div class="vc-snippet-doc">
            <h1>API</h1>
<p>全局配置：<code>silent / optionMergeStrategies / devtools / errorHandler / warnHandler / ignoredElements / keyCodes / performance / productionTip</code></p>
<p>全局API: <code> Vue.extend / Vue.nextTick / Vue.set / Vue.delete / Vue.directive / Vue.component / Vue.use / Vue.mixin / Vue.compile / Vue.observable / Vue.version</code></p>
<p>选项/数据：<code>data / props / propsData / methods / watch</code></p>
<p>选项/DOM: <code>el / template / render / renderError</code></p>
<p>选项/生命周期钩子：<code>beforeCreate / created / beforeMount / mounted / activated / deactivated / beforeDestroy / destroyed / errorCaptured</code></p>
<p>选项/资源：<code>directives / filters /components</code></p>
<p>选项/组合: <code>parent / mixins / extends / provide/inject</code></p>
<p>选项/其他：<code>name / delimiters / functional / model / inheritAttrs / comments</code></p>
<p>实例property:  <code>vm.$data / vm.$props / vm.$el / vm.$options / vm.$parent / vm.$root / vm.$children / vm.$slots / vm.$scopedSlots / vm.$refs / vm.$isServer / vm.$attrs / vm.$listeners</code></p>
<p>实例方法/数据：<code>vm.$watch / vm.$set / vm.$delete</code></p>
<p>实例方法/事件: <code>vm.$on / vm.$once / vm.$off / vm.$emit</code></p>
<p>实例方法/生命周期：<code>vm.$mount / vm.$forceUpdate / vm.$nextTick / vm.$destroy</code></p>
<p>指令：<code>v-text / v-html / v-show / v-if / v-else / v-else-if / v-for / v-on / v-bind / v-model / v-slot / v-pre / v-cloak / v-once</code></p>
<p>特殊attribute: <code>key / ref / is / slot废弃 / slot-scope废弃 / scope移除</code></p>
<p>内置的组件：<code>component / transition / transition-group / keep-alive / slot</code></p>
<p>VNode接口 / 服务器端渲染</p>
<h2>全局配置</h2>
<div class="tip">
<p><code>Vue.config</code>是一个对象，包含Vue的全局配置。可以在启动应用之前修改下列property:</p>
</div>
<h3>silent</h3>
<p>类型：<code>boolean</code></p>
<p>默认值：<code>false</code></p>
<p>用法：</p>
<pre class="hljs"><code><span class="hljs-title class_">Vue</span>.<span class="hljs-property">config</span>.<span class="hljs-property">silent</span> = <span class="hljs-literal">true</span>
</code></pre>
<div class="warning">
<p>取消<code>Vue</code>所有的日志与警告。</p>
</div>
<h3>optionMergeStrategies</h3>
<p>类型：<code>{ [key: string]: Function }</code></p>
<p>默认值：<code>{}</code></p>
<p>用法：</p>
<pre class="hljs"><code><span class="hljs-title class_">Vue</span>.<span class="hljs-property">config</span>.<span class="hljs-property">optionMergeStrategies</span>.<span class="hljs-property">_my_option</span> = <span class="hljs-keyword">function</span> (<span class="hljs-params">parent, child, vm</span>) {
  <span class="hljs-keyword">return</span> child + <span class="hljs-number">1</span>
}

<span class="hljs-keyword">const</span> <span class="hljs-title class_">Profile</span> = <span class="hljs-title class_">Vue</span>.<span class="hljs-title function_">extend</span>({
  <span class="hljs-attr">_my_option</span>: <span class="hljs-number">1</span>
})

<span class="hljs-comment">// Profile.options._my_option = 2</span>
</code></pre>
<div class="warning">
<p>自定义合并策略的选项。</p>
</div>
<p>合并策略选项分别接收在<strong>父实例</strong>和<strong>子实例</strong>上定义的该选项的值作为第一个和第二个参数，Vue 实例上下文被作为第三个参数传入。</p>
<h3>devtools</h3>
<p>类型：<code>boolean</code></p>
<p>默认值：<code>true</code>(生产版本为<code>false</code>)</p>
<p>用法：</p>
<pre class="hljs"><code><span class="hljs-comment">// 务必在加载 Vue 之后，立即同步设置以下内容</span>
<span class="hljs-title class_">Vue</span>.<span class="hljs-property">config</span>.<span class="hljs-property">devtools</span> = <span class="hljs-literal">true</span>
</code></pre>
<p>配置是否允许 <code>vue-devtools</code> 检查代码。开发版本默认为 true，生产版本默认为 <code>false</code>。生产版本设为 <code>true</code> 可以启用检查。</p>
<h3>errorHandler</h3>
<p>类型：<code>Function</code></p>
<p>默认值：<code>undefined</code></p>
<p>用法：</p>
<pre class="hljs"><code><span class="hljs-title class_">Vue</span>.<span class="hljs-property">config</span>.<span class="hljs-property">errorHandler</span> = <span class="hljs-keyword">function</span> (<span class="hljs-params">err, vm, info</span>) {
  <span class="hljs-comment">// handle error</span>
  <span class="hljs-comment">// `info` 是 Vue 特定的错误信息，比如错误所在的生命周期钩子</span>
}
</code></pre>
<p>指定组件的渲染和观察期间未捕获错误的处理函数。这个处理函数被调用时，可获取错误信息和Vue实例。</p>
<div class="tip">
<p>从 2.2.0 起，这个钩子也会捕获组件生命周期钩子里的错误。同样的，当这个钩子是 undefined 时，被捕获的错误会通过 console.error 输出而避免应用崩溃。</p>
</div>
<div class="tip">
<p>从 2.4.0 起，这个钩子也会捕获 Vue 自定义事件处理函数内部的错误了。</p>
</div>
<div class="tip">
<p>从 2.6.0 起，这个钩子也会捕获 v-on DOM 监听器内部抛出的错误。另外，如果任何被覆盖的钩子或处理函数返回一个 Promise 链 (例如 async 函数)，则来自其 Promise 链的错误也会被处理。</p>
</div>
<h3>warnHandler</h3>
<div class="tip">
<p>2.4.0 新增</p>
</div>
<p>类型：<code>Function</code></p>
<p>默认值：<code>undefined</code></p>
<p>用法：</p>
<pre class="hljs"><code><span class="hljs-title class_">Vue</span>.<span class="hljs-property">config</span>.<span class="hljs-property">warnHandler</span> = <span class="hljs-keyword">function</span> (<span class="hljs-params">msg, vm, trace</span>) {
  <span class="hljs-comment">// `trace`是组件的继承关系追踪</span>
}
</code></pre>
<p>为 Vue 的运行时警告赋予一个自定义处理函数。注意这只会在开发者环境下生效，在生产环境下它会被忽略。</p>
<h3>ignoredElements</h3>
<p>类型：<code>Array&lt;string | RegExp&gt;</code></p>
<p>默认值：<code>[]</code></p>
<p>用法：</p>
<pre class="hljs"><code><span class="hljs-title class_">Vue</span>.<span class="hljs-property">config</span>.<span class="hljs-property">ignoredElements</span> = [
  <span class="hljs-string">&#x27;my-custom-web-component&#x27;</span>,
  <span class="hljs-string">&#x27;another-web-component&#x27;</span>,
  <span class="hljs-comment">// 用一个 `RegExp` 忽略所有“ion-”开头的元素</span>
  <span class="hljs-comment">// 仅在 2.5+ 支持</span>
  <span class="hljs-regexp">/^ion-/</span>
]
</code></pre>
<p>须使 Vue 忽略在 Vue 之外的自定义元素 (e.g. 使用了 Web Components APIs)。否则，它会假设你忘记注册全局组件或者拼错了组件名称，从而抛出一个关于 Unknown custom element 的警告。</p>
<h3>keyCodes</h3>
<p>类型：<code>{ [key: string]: number | Array&lt;number&gt; }</code></p>
<p>默认值：<code>{}</code></p>
<p>用法：</p>
<pre class="hljs"><code><span class="hljs-title class_">Vue</span>.<span class="hljs-property">config</span>.<span class="hljs-property">keyCodes</span> = {
  <span class="hljs-attr">v</span>: <span class="hljs-number">86</span>,
  <span class="hljs-attr">f1</span>: <span class="hljs-number">112</span>,
  <span class="hljs-comment">// camelCase 不可用</span>
  <span class="hljs-attr">mediaPlayPause</span>: <span class="hljs-number">179</span>,
  <span class="hljs-comment">// 取而代之的是 kebab-case 且用双引号括起来</span>
  <span class="hljs-string">&quot;media-play-pause&quot;</span>: <span class="hljs-number">179</span>,
  <span class="hljs-attr">up</span>: [<span class="hljs-number">38</span>, <span class="hljs-number">87</span>]
}
&lt;input type=<span class="hljs-string">&quot;text&quot;</span> @keyup.<span class="hljs-property">media</span>-play-pause=<span class="hljs-string">&quot;method&quot;</span>&gt;
</code></pre>
<p>给 v-on 自定义键位别名。</p>
<h3>performance</h3>
<div class="tip">
<p>2.2.0 新增</p>
</div>
<p>类型：<code>boolean</code></p>
<p>默认值：<code>false</code> (自 2.2.3 起)</p>
<p>用法：</p>
<div class="warning">
<p>设置为 true 以在浏览器开发工具的性能/时间线面板中启用对组件初始化、编译、渲染和打补丁的性能追踪。只适用于开发模式和支持 performance.mark API 的浏览器上。</p>
</div>
<h3>productionTip</h3>
<div class="tip">
<p>2.2.0 新增</p>
</div>
<p>类型：<code>boolean</code></p>
<p>默认值：<code>true</code></p>
<p>用法：</p>
<p>设置为 false 以阻止 vue 在启动时生成生产提示。</p>
<h2>全局API</h2>
<h3>Vue.extend(options)</h3>
<p>参数：<code>{Object}options</code></p>
<p>用法：使用基础 <code>Vue</code> 构造器，创建一个“子类”。参数是一个包含组件选项的对象。</p>
<p><code>data</code>选项是特例，需要注意-在 <code>Vue.extend()</code>中它必须是函数。</p>
<pre class="hljs"><code>&lt;div id=<span class="hljs-string">&quot;mount-point&quot;</span>&gt;&lt;/div&gt;
</code></pre>
<pre class="hljs"><code><span class="hljs-comment">// 创建构造器</span>
<span class="hljs-keyword">var</span> <span class="hljs-title class_">Profile</span> = <span class="hljs-title class_">Vue</span>.<span class="hljs-title function_">extend</span>({
  <span class="hljs-attr">template</span>: <span class="hljs-string">&#x27;&lt;p&gt;{{data_5dd7b6a5}}{{data_6f5a6bf7}}aka{{data_e4b0814c}}&lt;/p&gt;`,
  data: function() {
    return {
      firstName: &#x27;</span>jeskson<span class="hljs-string">&#x27;,
      lastName: &#x27;</span>jeskson<span class="hljs-string">&#x27;,
      alias: &#x27;</span>dada<span class="hljs-string">&#x27;
    }
  }
})
// 创建Profile实例，并挂载到一个元素上
new Profile().$mount(&#x27;</span>#mount-point<span class="hljs-string">&#x27;)
</span></code></pre>
<p>结果如下：</p>
<pre class="hljs"><code>&lt;p&gt;jeskson jeskson aka dada&lt;/p&gt;
</code></pre>
<h3>Vue.nextTick([callback, context])</h3>
<p>参数：<code>{Function}[callback]</code> - <code>{Object}[context]</code></p>
<p>用法：在下次<code>DOM</code>更新循环结束之后执行延迟回调。在修改数据之后立即使用这个方法，获取更新后的<code>DOM</code>。</p>
<pre class="hljs"><code><span class="hljs-comment">// 修改数据</span>
vm.<span class="hljs-property">msg</span> = <span class="hljs-string">&#x27;Hello&#x27;</span>
<span class="hljs-comment">// DOM 还没有更新</span>
<span class="hljs-title class_">Vue</span>.<span class="hljs-title function_">nextTick</span>(<span class="hljs-keyword">function</span>(<span class="hljs-params"></span>) {
  <span class="hljs-comment">// DOM 更新了</span>
})
<span class="hljs-comment">// 作为一个Promise使用</span>
<span class="hljs-title class_">Vue</span>.<span class="hljs-title function_">nextTick</span>()
.<span class="hljs-title function_">then</span>(<span class="hljs-keyword">function</span>(<span class="hljs-params"></span>) {
  <span class="hljs-comment">// DOM 更新了</span>
})
</code></pre>
<div class="tip">
<p>2.1.0 起新增：如果没有提供回调且在支持 Promise 的环境中，则返回一个 Promise。请注意 Vue 不自带 Promise 的 polyfill，所以如果你的目标浏览器不原生支持 Promise (IE：你们都看我干嘛)，你得自己提供 polyfill。</p>
</div>
<h3>Vue.set</h3>
<p><code>Vue.set( target, propertyName/index, value )</code></p>
<p>参数：</p>
<p><code>{Object | Array} target</code></p>
<p><code>{string | number} propertyName/index</code></p>
<p><code>{any} value</code></p>
<div class="tip">
<p>返回值：设置的值。</p>
</div>
<p>用法：</p>
<p>向响应式对象中添加一个 <code>property</code>，并确保这个新 <code>property</code> 同样是响应式的，且触发视图更新。它必须用于向响应式对象上添加新 <code>property</code>，因为 Vue 无法探测普通的新增 <code>property</code> (比如 <code>this.myObject.newProperty = 'hi'</code>)</p>
<div class="warning">
<p>注意对象不能是 Vue 实例，或者 Vue 实例的根数据对象。</p>
</div>
<h3>Vue.delete(target, propertyName/index)</h3>
<p>参数：</p>
<p><code>{Object | Array} target</code></p>
<p><code>{string | number} propertyName/index</code></p>
<div class="tip">
<p>仅在 2.2.0+ 版本中支持 Array + index 用法。</p>
</div>
<p>用法：</p>
<p>删除对象的 property。如果对象是响应式的，确保删除能触发更新视图。这个方法主要用于避开 Vue 不能检测到 property 被删除的限制，但是你应该很少会使用它。</p>
<div class="tip">
<p>在 2.2.0+ 中同样支持在数组上工作。</p>
</div>
<div class="warning">
<p>目标对象不能是一个 Vue 实例或 Vue 实例的根数据对象。</p>
</div>
<h3>Vue.directive(id, [definition])</h3>
<p>参数：</p>
<p><code>{string} id</code></p>
<p><code>{Function | Object} [definition]</code></p>
<p>用法：注册或获取全局指令</p>
<pre class="hljs"><code><span class="hljs-comment">// 注册</span>
<span class="hljs-title class_">Vue</span>.<span class="hljs-title function_">directive</span>(<span class="hljs-string">&#x27;my-directive&#x27;</span>, {
  <span class="hljs-attr">bind</span>: <span class="hljs-keyword">function</span>(<span class="hljs-params"></span>) {},
  <span class="hljs-attr">inserted</span>: <span class="hljs-keyword">function</span>(<span class="hljs-params"></span>) {},
  update： <span class="hljs-keyword">function</span>(<span class="hljs-params"></span>) {},
  <span class="hljs-attr">componentUpdated</span>: <span class="hljs-keyword">function</span>(<span class="hljs-params"></span>) {},
  <span class="hljs-attr">unbind</span>: <span class="hljs-keyword">function</span>(<span class="hljs-params"></span>) {}
})

<span class="hljs-comment">// 注册（指令函数）</span>
<span class="hljs-title class_">Vue</span>.<span class="hljs-title function_">directive</span>(<span class="hljs-string">&#x27;my-directive&#x27;</span>, <span class="hljs-keyword">function</span>(<span class="hljs-params"></span>) {
  <span class="hljs-comment">// 这里将会被 `bind` 和 `update` 调用</span>
})

<span class="hljs-comment">// getter，返回已注册的指令</span>
<span class="hljs-keyword">var</span> myDirective = <span class="hljs-title class_">Vue</span>.<span class="hljs-title function_">directive</span>(<span class="hljs-string">&#x27;my-directive&#x27;</span>)
</code></pre>
<h3>Vue.filter(id,[definition])</h3>
<p>参数：</p>
<p><code>{string} id</code></p>
<p><code>{Function} [definition]</code></p>
<p>用法：注册或获取全局过滤器</p>
<pre class="hljs"><code><span class="hljs-comment">// 注册</span>
<span class="hljs-title class_">Vue</span>.<span class="hljs-title function_">filter</span>(<span class="hljs-string">&#x27;my-filter`, function(value) {
  // 返回处理后的值
})

// getter,返回已注册的过滤器
var myFilter = Vue.filter(&#x27;</span>my-filter<span class="hljs-string">&#x27;)
</span></code></pre>
<h3>Vue.component(id, [definition])</h3>
<p>参数：</p>
<p><code>{string} id</code></p>
<p><code>{Function | Object} [definition]</code></p>
<p>用法：</p>
<p>注册或获取全局文件。注册还会自动使用给定的id设置组件的名称</p>
<pre class="hljs"><code><span class="hljs-comment">// 注册组件，传入一个扩展过的构造器</span>
<span class="hljs-title class_">Vue</span>.<span class="hljs-title function_">component</span>(<span class="hljs-string">&#x27;my-component`, Vue.extend({ /*...*/ }))

// 注册组件，传入一个选项对象（自动调用Vue.extend)
Vue.component(&#x27;</span>my-component<span class="hljs-string">&#x27;, { /*...*/ })

// 获取注册的组件（始终返回构造器）
var MyComponent = Vue.component(&#x27;</span>my-component<span class="hljs-string">&#x27;)
</span></code></pre>
<h3>Vue.use(plugin)</h3>
<p>参数：</p>
<p><code>{Object | Function} plugin</code></p>
<p>用法：</p>
<p>安装 Vue.js 插件。如果插件是一个对象，必须提供 install 方法。如果插件是一个函数，它会被作为 install 方法。install 方法调用时，会将 Vue 作为参数传入。</p>
<p>该方法需要在调用 new Vue() 之前被调用。</p>
<p>当 install 方法被同一个插件多次调用，插件将只会被安装一次。</p>
<h3>Vue.mixin( mixin )</h3>
<p>参数：</p>
<p><code>{Object} mixin</code></p>
<p>用法：</p>
<p>全局注册一个混入，影响注册之后所有创建的每个 Vue 实例。插件作者可以使用混入，向组件注入自定义的行为。不推荐在应用代码中使用。</p>
<h3>Vue.compile( template )</h3>
<p>参数：</p>
<p><code>{string} template</code></p>
<p>用法：</p>
<p>将一个模板字符串编译成 render 函数。只在完整版时可用。</p>
<pre class="hljs"><code><span class="hljs-keyword">var</span> res = <span class="hljs-title class_">Vue</span>.<span class="hljs-title function_">compile</span>(<span class="hljs-string">&#x27;&lt;div&gt;&lt;span&gt;{{data_2175fa29}}&lt;/span&gt;&lt;/div&gt;&#x27;</span>)

<span class="hljs-keyword">new</span> <span class="hljs-title class_">Vue</span>({
  <span class="hljs-attr">data</span>: {
    <span class="hljs-attr">msg</span>: <span class="hljs-string">&#x27;hello&#x27;</span>
  },
  <span class="hljs-attr">render</span>: res.<span class="hljs-property">render</span>,
  <span class="hljs-attr">staticRenderFns</span>: res.<span class="hljs-property">staticRenderFns</span>
})
</code></pre>
<h3>Vue.observable(object)</h3>
<div class="tip">
<p>2.6.0新增</p>
</div>
<p>参数：<code>{Object} Object</code></p>
<p>用法：让一个对象可响应。Vue内部会用它来处理data函数返回的对象</p>
<p>返回的对象可以直接用于渲染函数和计算属性内，并且会在发生变更时触发相应的更新。也可以作为最小化的跨组件状态存储器，用于简单的场景：</p>
<pre class="hljs"><code><span class="hljs-keyword">const</span> state = <span class="hljs-title class_">Vue</span>.<span class="hljs-title function_">observable</span>({ <span class="hljs-attr">count</span>: <span class="hljs-number">0</span> })

<span class="hljs-keyword">const</span> <span class="hljs-title class_">Demo</span> = {
  <span class="hljs-title function_">render</span>(<span class="hljs-params">h</span>) {
    <span class="hljs-keyword">return</span> <span class="hljs-title function_">h</span>(<span class="hljs-string">&#x27;button&#x27;</span>, {
      <span class="hljs-attr">on</span>: { <span class="hljs-attr">click</span>: <span class="hljs-function">() =&gt;</span> { state.<span class="hljs-property">count</span>++ }}
    }, <span class="hljs-string">`count is: <span class="hljs-subst">${state.count}</span>`</span>)
  }
}
</code></pre>
<div class="warning">
<p>在 <code>Vue 2.x</code> 中，被传入的对象会直接被 <code>Vue.observable</code> 变更，所以如这里展示的，它和被返回的对象是同一个对象。在 <code>Vue 3.x</code> 中，则会返回一个可响应的代理，而对源对象直接进行变更仍然是不可响应的。因此，为了向前兼容，我们推荐始终操作使用 <code>Vue.observable</code> 返回的对象，而不是传入源对象。</p>
</div>
<h3>Vue.version</h3>
<p>细节：提供字符串形式的 Vue 安装版本号。这对社区的插件和组件来说非常有用，你可以根据不同的版本号采取不同的策略。</p>
<p>用法：</p>
<pre class="hljs"><code><span class="hljs-keyword">var</span> version = <span class="hljs-title class_">Number</span>(<span class="hljs-title class_">Vue</span>.<span class="hljs-property">version</span>.<span class="hljs-title function_">split</span>(<span class="hljs-string">&#x27;.&#x27;</span>)[<span class="hljs-number">0</span>])

<span class="hljs-keyword">if</span> (version === <span class="hljs-number">2</span>) {
  <span class="hljs-comment">// Vue v2.x.x</span>
} <span class="hljs-keyword">else</span> <span class="hljs-keyword">if</span> (version === <span class="hljs-number">1</span>) {
  <span class="hljs-comment">// Vue v1.x.x</span>
} <span class="hljs-keyword">else</span> {
  <span class="hljs-comment">// Unsupported versions of Vue</span>
}
</code></pre>
<h2>选项 / 数据</h2>
<h3>data</h3>
<p>类型：<code>Object | Function</code></p>
<p>限制：组件的定义只接受 <code>function</code>。</p>
<p>详细：</p>
<p><code>Vue</code> 实例的数据对象。<code>Vue</code> 会递归地把 <code>data</code> 的 <code>property</code> 转换为 <code>getter/setter</code>，从而让 <code>data</code> 的 <code>property</code> 能够响应数据变化。对象必须是纯粹的对象 (含有零个或多个的 <code>key/value</code>对)：浏览器 <code>API</code> 创建的原生对象，原型上的 <code>property</code> 会被忽略。大概来说，<code>data</code> 应该只能是数据 - 不推荐观察拥有状态行为的对象。</p>
<p>一旦观察过，你就无法在根数据对象上添加响应式 <code>property</code>。因此推荐在创建实例之前，就声明所有的根级响应式 <code>property</code>。</p>
<p>实例创建之后，可以通过 <code>vm.$data</code> 访问原始数据对象。<code>Vue</code> 实例也代理了 <code>data</code> 对象上所有的 <code>property</code>，因此访问 <code>vm.a</code> 等价于访问 <code>vm.$data.a</code>。</p>
<p>以 <code>_ 或 $</code> 开头的 <code>property</code> 不会被 Vue 实例代理，因为它们可能和 <code>Vue</code> 内置的 <code>property、API</code> 方法冲突。你可以使用例如 <code>vm.$data._property</code> 的方式访问这些 <code>property</code>。</p>
<p>当一个组件被定义，<code>data</code> 必须声明为返回一个初始数据对象的函数，因为组件可能被用来创建多个实例。如果 <code>data</code> 仍然是一个纯粹的对象，则所有的实例将共享引用同一个数据对象！通过提供 <code>data</code> 函数，每次创建一个新实例后，我们能够调用 <code>data</code> 函数，从而返回初始数据的一个全新副本数据对象。</p>
<p>如果需要，可以通过将 <code>vm.$data</code> 传入 <code>JSON.parse(JSON.stringify(...))</code> 得到深拷贝的原始数据对象。</p>
<p>示例：</p>
<pre class="hljs"><code><span class="hljs-keyword">var</span> data = { <span class="hljs-attr">a</span>: <span class="hljs-number">1</span> }

<span class="hljs-comment">// 直接创建一个实例</span>
<span class="hljs-keyword">var</span> vm = <span class="hljs-keyword">new</span> <span class="hljs-title class_">Vue</span>({
  <span class="hljs-attr">data</span>: data
})
vm.<span class="hljs-property">a</span> <span class="hljs-comment">// =&gt; 1</span>
vm.<span class="hljs-property">$data</span> === data <span class="hljs-comment">// =&gt; true</span>

<span class="hljs-comment">// Vue.extend() 中 data 必须是函数</span>
<span class="hljs-keyword">var</span> <span class="hljs-title class_">Component</span> = <span class="hljs-title class_">Vue</span>.<span class="hljs-title function_">extend</span>({
  <span class="hljs-attr">data</span>: <span class="hljs-keyword">function</span> (<span class="hljs-params"></span>) {
    <span class="hljs-keyword">return</span> { <span class="hljs-attr">a</span>: <span class="hljs-number">1</span> }
  }
})
</code></pre>
<p>注意，如果你为 data property 使用了箭头函数，则 this 不会指向这个组件的实例，不过你仍然可以将其实例作为函数的第一个参数来访问。</p>
<pre class="hljs"><code><span class="hljs-attr">data</span>: <span class="hljs-function"><span class="hljs-params">vm</span> =&gt;</span> ({ <span class="hljs-attr">a</span>: vm.<span class="hljs-property">myProp</span> })
</code></pre>
<h3>props</h3>
<p>类型：<code>Array&lt;string&gt; | Object</code></p>
<p>详细：</p>
<p><code>props</code> 可以是数组或对象，用于接收来自父组件的数据。props 可以是简单的数组，或者使用对象作为替代，对象允许配置高级选项，如类型检测、自定义验证和设置默认值。</p>
<p>你可以基于对象的语法使用以下选项：</p>
<p><code>type</code>：可以是下列原生构造函数中的一种：<code>String、Number、Boolean、Array、Object、Date、Function、Symbol</code>、任何自定义构造函数、或上述内容组成的数组。会检查一个 <code>prop</code> 是否是给定的类型，否则抛出警告。Prop 类型的更多信息在此。</p>
<p><code>default：any</code></p>
<p>为该 prop 指定一个默认值。如果该 prop 没有被传入，则换做用这个值。对象或数组的默认值必须从一个工厂函数返回。</p>
<p><code>required：Boolean</code></p>
<p>定义该 prop 是否是必填项。在非生产环境中，如果这个值为 <code>truthy</code> 且该 <code>prop</code> 没有被传入的，则一个控制台警告将会被抛出。</p>
<p><code>validator：Function</code></p>
<p>自定义验证函数会将该 <code>prop</code> 的值作为唯一的参数代入。在非生产环境下，如果该函数返回一个 <code>falsy</code> 的值 (也就是验证失败)，一个控制台警告将会被抛出。你可以在这里查阅更多 <code>prop</code> 验证的相关信息。</p>
<p>示例：</p>
<pre class="hljs"><code><span class="hljs-comment">// 简单语法</span>
<span class="hljs-title class_">Vue</span>.<span class="hljs-title function_">component</span>(<span class="hljs-string">&#x27;props-demo-simple&#x27;</span>, {
  <span class="hljs-attr">props</span>: [<span class="hljs-string">&#x27;size&#x27;</span>, <span class="hljs-string">&#x27;myMessage&#x27;</span>]
})

<span class="hljs-comment">// 对象语法，提供验证</span>
<span class="hljs-title class_">Vue</span>.<span class="hljs-title function_">component</span>(<span class="hljs-string">&#x27;props-demo-advanced&#x27;</span>, {
  <span class="hljs-attr">props</span>: {
    <span class="hljs-comment">// 检测类型</span>
    <span class="hljs-attr">height</span>: <span class="hljs-title class_">Number</span>,
    <span class="hljs-comment">// 检测类型 + 其他验证</span>
    <span class="hljs-attr">age</span>: {
      <span class="hljs-attr">type</span>: <span class="hljs-title class_">Number</span>,
      <span class="hljs-attr">default</span>: <span class="hljs-number">0</span>,
      <span class="hljs-attr">required</span>: <span class="hljs-literal">true</span>,
      <span class="hljs-attr">validator</span>: <span class="hljs-keyword">function</span> (<span class="hljs-params">value</span>) {
        <span class="hljs-keyword">return</span> value &gt;= <span class="hljs-number">0</span>
      }
    }
  }
})
</code></pre>
<h3>propsData</h3>
<p>类型：<code>{ [key: string]: any }</code></p>
<p>限制：只用于 <code>new</code> 创建的实例中。</p>
<p>详细：</p>
<p>创建实例时传递 <code>props</code>。主要作用是方便测试。</p>
<p>示例：</p>
<pre class="hljs"><code><span class="hljs-keyword">var</span> <span class="hljs-title class_">Comp</span> = <span class="hljs-title class_">Vue</span>.<span class="hljs-title function_">extend</span>({
  <span class="hljs-attr">props</span>: [<span class="hljs-string">&#x27;msg&#x27;</span>],
  <span class="hljs-attr">template</span>: <span class="hljs-string">&#x27;&lt;div&gt;{{data_2175fa29}}&lt;/div&gt;&#x27;</span>
})

<span class="hljs-keyword">var</span> vm = <span class="hljs-keyword">new</span> <span class="hljs-title class_">Comp</span>({
  <span class="hljs-attr">propsData</span>: {
    <span class="hljs-attr">msg</span>: <span class="hljs-string">&#x27;hello&#x27;</span>
  }
})
</code></pre>
<h3>computed</h3>
<p>类型：<code>{ [key: string]: Function | { get: Function, set: Function } }</code></p>
<p>详细：</p>
<p>计算属性将被混入到 <code>Vue</code> 实例中。所有 <code>getter</code> 和 <code>setter</code> 的 <code>this</code> 上下文自动地绑定为 <code>Vue</code> 实例。</p>
<p>注意如果你为一个计算属性使用了箭头函数，则 this 不会指向这个组件的实例，不过你仍然可以将其实例作为函数的第一个参数来访问。</p>
<pre class="hljs"><code><span class="hljs-attr">computed</span>: {
  <span class="hljs-attr">aDouble</span>: <span class="hljs-function"><span class="hljs-params">vm</span> =&gt;</span> vm.<span class="hljs-property">a</span> * <span class="hljs-number">2</span>
}
</code></pre>
<p>计算属性的结果会被缓存，除非依赖的响应式 <code>property</code> 变化才会重新计算。注意，如果某个依赖 (比如非响应式 <code>property</code>) 在该实例范畴之外，则计算属性是不会被更新的。</p>
<p>示例：</p>
<pre class="hljs"><code><span class="hljs-keyword">var</span> vm = <span class="hljs-keyword">new</span> <span class="hljs-title class_">Vue</span>({
  <span class="hljs-attr">data</span>: { <span class="hljs-attr">a</span>: <span class="hljs-number">1</span> },
  <span class="hljs-attr">computed</span>: {
    <span class="hljs-comment">// 仅读取</span>
    <span class="hljs-attr">aDouble</span>: <span class="hljs-keyword">function</span> (<span class="hljs-params"></span>) {
      <span class="hljs-keyword">return</span> <span class="hljs-variable language_">this</span>.<span class="hljs-property">a</span> * <span class="hljs-number">2</span>
    },
    <span class="hljs-comment">// 读取和设置</span>
    <span class="hljs-attr">aPlus</span>: {
      <span class="hljs-attr">get</span>: <span class="hljs-keyword">function</span> (<span class="hljs-params"></span>) {
        <span class="hljs-keyword">return</span> <span class="hljs-variable language_">this</span>.<span class="hljs-property">a</span> + <span class="hljs-number">1</span>
      },
      <span class="hljs-attr">set</span>: <span class="hljs-keyword">function</span> (<span class="hljs-params">v</span>) {
        <span class="hljs-variable language_">this</span>.<span class="hljs-property">a</span> = v - <span class="hljs-number">1</span>
      }
    }
  }
})
vm.<span class="hljs-property">aPlus</span>   <span class="hljs-comment">// =&gt; 2</span>
vm.<span class="hljs-property">aPlus</span> = <span class="hljs-number">3</span>
vm.<span class="hljs-property">a</span>       <span class="hljs-comment">// =&gt; 2</span>
vm.<span class="hljs-property">aDouble</span> <span class="hljs-comment">// =&gt; 4</span>
</code></pre>
<h3>methods</h3>
<p>类型：<code>{ [key: string]: Function }</code></p>
<p>详细：</p>
<p><code>methods</code> 将被混入到 <code>Vue</code> 实例中。可以直接通过 <code>VM</code> 实例访问这些方法，或者在指令表达式中使用。方法中的 <code>this</code> 自动绑定为 <code>Vue</code> 实例。</p>
<div class="warning">
<p>注意，不应该使用箭头函数来定义 <code>method</code> 函数 (例如 <code>plus: () =&gt; this.a++</code>)。理由是箭头函数绑定了父级作用域的上下文，所以 <code>this</code> 将不会按照期望指向 <code>Vue</code> 实例，<code>this.a</code> 将是 <code>undefined</code>。</p>
</div>
<p>示例：</p>
<pre class="hljs"><code><span class="hljs-keyword">var</span> vm = <span class="hljs-keyword">new</span> <span class="hljs-title class_">Vue</span>({
  <span class="hljs-attr">data</span>: { <span class="hljs-attr">a</span>: <span class="hljs-number">1</span> },
  <span class="hljs-attr">methods</span>: {
    <span class="hljs-attr">plus</span>: <span class="hljs-keyword">function</span> (<span class="hljs-params"></span>) {
      <span class="hljs-variable language_">this</span>.<span class="hljs-property">a</span>++
    }
  }
})
vm.<span class="hljs-title function_">plus</span>()
vm.<span class="hljs-property">a</span> <span class="hljs-comment">// 2</span>
</code></pre>
<h3>watch</h3>
<p>类型：<code>{ [key: string]: string | Function | Object | Array }</code></p>
<p>详细：</p>
<p>一个对象，键是需要观察的表达式，值是对应回调函数。值也可以是方法名，或者包含选项的对象。<code>Vue</code> 实例将会在实例化时调用 <code>$watch()</code>，遍历 <code>watch</code> 对象的每一个 <code>property</code>。</p>
<p>示例：</p>
<pre class="hljs"><code><span class="hljs-keyword">var</span> vm = <span class="hljs-keyword">new</span> <span class="hljs-title class_">Vue</span>({
  <span class="hljs-attr">data</span>: {
    <span class="hljs-attr">a</span>: <span class="hljs-number">1</span>,
    <span class="hljs-attr">b</span>: <span class="hljs-number">2</span>,
    <span class="hljs-attr">c</span>: <span class="hljs-number">3</span>,
    <span class="hljs-attr">d</span>: <span class="hljs-number">4</span>,
    <span class="hljs-attr">e</span>: {
      <span class="hljs-attr">f</span>: {
        <span class="hljs-attr">g</span>: <span class="hljs-number">5</span>
      }
    }
  },
  <span class="hljs-attr">watch</span>: {
    <span class="hljs-attr">a</span>: <span class="hljs-keyword">function</span> (<span class="hljs-params">val, oldVal</span>) {
      <span class="hljs-variable language_">console</span>.<span class="hljs-title function_">log</span>(<span class="hljs-string">&#x27;new: %s, old: %s&#x27;</span>, val, oldVal)
    },
    <span class="hljs-comment">// 方法名</span>
    <span class="hljs-attr">b</span>: <span class="hljs-string">&#x27;someMethod&#x27;</span>,
    <span class="hljs-comment">// 该回调会在任何被侦听的对象的 property 改变时被调用，不论其被嵌套多深</span>
    <span class="hljs-attr">c</span>: {
      <span class="hljs-attr">handler</span>: <span class="hljs-keyword">function</span> (<span class="hljs-params">val, oldVal</span>) { <span class="hljs-comment">/* ... */</span> },
      <span class="hljs-attr">deep</span>: <span class="hljs-literal">true</span>
    },
    <span class="hljs-comment">// 该回调将会在侦听开始之后被立即调用</span>
    <span class="hljs-attr">d</span>: {
      <span class="hljs-attr">handler</span>: <span class="hljs-string">&#x27;someMethod&#x27;</span>,
      <span class="hljs-attr">immediate</span>: <span class="hljs-literal">true</span>
    },
    <span class="hljs-comment">// 你可以传入回调数组，它们会被逐一调用</span>
    <span class="hljs-attr">e</span>: [
      <span class="hljs-string">&#x27;handle1&#x27;</span>,
      <span class="hljs-keyword">function</span> <span class="hljs-title function_">handle2</span> (val, oldVal) { <span class="hljs-comment">/* ... */</span> },
      {
        <span class="hljs-attr">handler</span>: <span class="hljs-keyword">function</span> <span class="hljs-title function_">handle3</span> (val, oldVal) { <span class="hljs-comment">/* ... */</span> },
        <span class="hljs-comment">/* ... */</span>
      }
    ],
    <span class="hljs-comment">// watch vm.e.f&#x27;s value: {g: 5}</span>
    <span class="hljs-string">&#x27;e.f&#x27;</span>: <span class="hljs-keyword">function</span> (<span class="hljs-params">val, oldVal</span>) { <span class="hljs-comment">/* ... */</span> }
  }
})
vm.<span class="hljs-property">a</span> = <span class="hljs-number">2</span> <span class="hljs-comment">// =&gt; new: 2, old: 1</span>
</code></pre>
<div class="warning">
<p>注意，不应该使用箭头函数来定义 <code>watcher</code> 函数 (例如 <code>searchQuery: newValue =&gt; this.updateAutocomplete(newValue)</code>)。理由是箭头函数绑定了父级作用域的上下文，所以 <code>this</code> 将不会按照期望指向 <code>Vue</code> 实例，<code>this.updateAutocomplete</code> 将是 <code>undefined</code>。</p>
</div>
<h2>选项/DOM</h2>
<h3>el</h3>
<p>类型：<code>string | Element</code></p>
<p>限制：只在用 <code>new</code> 创建实例时生效</p>
<p>详细：</p>
<p>提供一个在页面上已存在的<code>DOM</code>元素 作为 <code>Vue</code>实例的挂载目标，可以是css选择器，也可以是一个HTMLElement实例。</p>
<p>在实例挂载之后，元素可以用 <code>vm.$el</code> 访问。</p>
<p>如果在实例化时存在这个选项，实例将立即进入编译过程，否则，需要显示调用 <code>vm.$mount()</code> 手动开启编译。</p>
<div class="warning">
<p>提供的元素只能作为挂载点。不同于Vue1.x，所有的挂载元素会被Vue生成的DOM替换。因此不推荐挂载root实例到<code>&lt;html&gt;</code>或者<code>&lt;body&gt;</code>上。</p>
</div>
<div class="warning">
<p>如果 <code>render</code> 函数和 <code>template property</code> 都不存在，挂载 DOM 元素的 HTML 会被提取出来用作模板，此时，必须使用 <code>Runtime + Compiler</code> 构建的 Vue 库。</p>
</div>
<h3>template</h3>
<p>类型：<code>string</code></p>
<p>详细：</p>
<p>一个字符串模板作为 <code>Vue</code> 实例的标识使用。模板将会替换挂载的元素。挂载元素的内容都将被忽略，除非模板的内容有分发插槽。</p>
<p>如果值以 <code>#</code> 开始，则它将被用作选择符，并使用匹配元素的 <code>innerHTML</code> 作为模板。常用的技巧是用 <code>&lt;script type=&quot;x-template&quot;&gt;</code> 包含模板。</p>
<div class="warning">
<p>出于安全考虑，你应该只使用你信任的 <code>Vue</code> 模板。避免使用其他人生成的内容作为你的模板。</p>
</div>
<div class="warning">
<p>如果 <code>Vue</code> 选项中包含渲染函数，该模板将被忽略。</p>
</div>
<h3>render</h3>
<p>类型：<code>(createElement: () =&gt; VNode) =&gt; VNode</code></p>
<p>详细：</p>
<p>字符串模板的代替方案，允许你发挥 <code>JavaScript</code> 最大的编程能力。该渲染函数接收一个 <code>createElement</code> 方法作为第一个参数用来创建 <code>VNode</code>。</p>
<p>如果组件是一个函数组件，渲染函数还会接收一个额外的 <code>context</code> 参数，为没有实例的函数组件提供上下文信息。</p>
<div class="warning">
<p><code>Vue</code> 选项中的 <code>render</code> 函数若存在，则 <code>Vue</code> 构造函数不会从 <code>template</code> 选项或通过 <code>el</code> 选项指定的挂载元素中提取出的 <code>HTML</code> 模板编译渲染函数。</p>
</div>
<h3>renderError</h3>
<div class="tip">
<p>2.2.0 新增</p>
</div>
<p>类型：<code>(createElement: () =&gt; VNode, error: Error) =&gt; VNode</code></p>
<p>详细：</p>
<p>只在开发者环境下工作。</p>
<p>当 <code>render</code> 函数遭遇错误时，提供另外一种渲染输出。其错误将会作为第二个参数传递到 <code>renderError</code>。这个功能配合 <code>hot-reload</code> 非常实用。</p>
<p>示例：</p>
<pre class="hljs"><code><span class="hljs-keyword">new</span> <span class="hljs-title class_">Vue</span>({
  render (h) {
    <span class="hljs-keyword">throw</span> <span class="hljs-keyword">new</span> <span class="hljs-title class_">Error</span>(<span class="hljs-string">&#x27;oops&#x27;</span>)
  },
  renderError (h, err) {
    <span class="hljs-keyword">return</span> <span class="hljs-title function_">h</span>(<span class="hljs-string">&#x27;pre&#x27;</span>, { <span class="hljs-attr">style</span>: { <span class="hljs-attr">color</span>: <span class="hljs-string">&#x27;red&#x27;</span> }}, err.<span class="hljs-property">stack</span>)
  }
}).$mount(<span class="hljs-string">&#x27;#app&#x27;</span>)
</code></pre>
<h2>选项/生命周期钩子</h2>
<div class="warning">
<p>所有生命周期钩子的 <code>this</code> 上下文将自动绑定至实例中，因此你可以访问 <code>data、computed 和 methods</code>。这意味着你不应该使用箭头函数来定义一个生命周期方法 (例如 <code>created: () =&gt; this.fetchTodos()</code>)。因为箭头函数绑定了父级上下文，所以 <code>this</code> 不会指向预期的组件实例，并且<code>this.fetchTodos</code> 将会是 <code>undefined</code>。</p>
</div>
<h3>beforeCreate</h3>
<p>类型：<code>Function</code></p>
<p>详细：在实例初始化之后，进行数据侦听和事件/侦听器的配置之前同步调用。</p>
<h3>created</h3>
<p>类型：<code>Function</code></p>
<p>详细：在实例创建完成后被立即同步调用。在这一步中，实例已完成对选项的处理，意味着以下内容已被配置完毕：数据侦听、计算属性、方法、事件/侦听器的回调函数。然而，挂载阶段还没开始，且 <code>$el property</code> 目前尚不可用。</p>
<h3>beforeMount</h3>
<p>类型：<code>Function</code></p>
<p>详细：在挂载开始之前被调用：相关的 <code>render</code> 函数首次被调用。</p>
<p><strong>该钩子在服务器端渲染期间不被调用。</strong></p>
<h3>mounted</h3>
<p>类型：<code>Function</code></p>
<p>详细：实例被挂载后调用，这时 <code>el</code> 被新创建的 <code>vm.$el</code> 替换了。如果根实例挂载到了一个文档内的元素上，当 <code>mounted</code> 被调用时 <code>vm.$el</code> 也在文档内。</p>
<p>注意 <code>mounted</code> 不会保证所有的子组件也都被挂载完成。如果你希望等到整个视图都渲染完毕再执行某些操作，可以在 <code>mounted</code> 内部使用 <code>vm.$nextTick</code>：</p>
<pre class="hljs"><code><span class="hljs-attr">mounted</span>: <span class="hljs-keyword">function</span> (<span class="hljs-params"></span>) {
  <span class="hljs-variable language_">this</span>.$nextTick(<span class="hljs-keyword">function</span> (<span class="hljs-params"></span>) {
    <span class="hljs-comment">// 仅在整个视图都被渲染之后才会运行的代码</span>
  })
}
</code></pre>
<p><strong>该钩子在服务器端渲染期间不被调用。</strong></p>
<h3>beforeUpdate</h3>
<p>类型：<code>Function</code></p>
<p>详细：在数据发生改变后，DOM 被更新之前被调用。这里适合在现有 DOM 将要被更新之前访问它，比如移除手动添加的事件监听器。</p>
<p><strong>该钩子在服务器端渲染期间不被调用，因为只有初次渲染会在服务器端进行。</strong></p>
<h3>updated</h3>
<p>类型：<code>Function</code></p>
<p>详细：</p>
<p>在数据更改导致的虚拟 <code>DOM</code> 重新渲染和更新完毕之后被调用。</p>
<p>当这个钩子被调用时，组件 <code>DOM</code> 已经更新，所以你现在可以执行依赖于 <code>DOM</code> 的操作。然而在大多数情况下，你应该避免在此期间更改状态。如果要相应状态改变，通常最好使用计算属性或 <code>watcher</code> 取而代之。</p>
<p>注意，<code>updated</code> 不会保证所有的子组件也都被重新渲染完毕。如果你希望等到整个视图都渲染完毕，可以在 <code>updated</code> 里使用 <code>vm.$nextTick</code>：</p>
<pre class="hljs"><code><span class="hljs-attr">updated</span>: <span class="hljs-keyword">function</span> (<span class="hljs-params"></span>) {
  <span class="hljs-variable language_">this</span>.$nextTick(<span class="hljs-keyword">function</span> (<span class="hljs-params"></span>) {
    <span class="hljs-comment">//  仅在整个视图都被重新渲染之后才会运行的代码     </span>
  })
}
</code></pre>
<p><strong>该钩子在服务器端渲染期间不被调用。</strong></p>
<h3>activated</h3>
<p>类型：<code>Function</code></p>
<p>详细：</p>
<p>被 <code>keep-alive</code> 缓存的组件激活时调用。</p>
<p><strong>该钩子在服务器端渲染期间不被调用。</strong></p>
<h3>deactivated</h3>
<p>类型：<code>Function</code></p>
<p>详细：</p>
<p>被 <code>keep-alive</code> 缓存的组件失活时调用。</p>
<p><strong>该钩子在服务器端渲染期间不被调用。</strong></p>
<h3>beforeDestroy</h3>
<p>类型：<code>Function</code></p>
<p>详细：</p>
<p>实例销毁之前调用。在这一步，实例仍然完全可用。</p>
<p><strong>该钩子在服务器端渲染期间不被调用。</strong></p>
<h3>destroyed</h3>
<p>类型：<code>Function</code></p>
<p>详细：</p>
<p>实例销毁后调用。该钩子被调用后，对应 <code>Vue</code> 实例的所有指令都被解绑，所有的事件监听器被移除，所有的子实例也都被销毁。</p>
<p><strong>该钩子在服务器端渲染期间不被调用。</strong></p>
<h3>errorCaptured</h3>
<div class="tip">
<p>2.5.0+ 新增</p>
</div>
<p>类型：<code>(err: Error, vm: Component, info: string) =&gt; ?boolean</code></p>
<p>详细：</p>
<p>在捕获一个来自后代组件的错误时被调用。此钩子会收到三个参数：错误对象、发生错误的组件实例以及一个包含错误来源信息的字符串。此钩子可以返回 <code>false</code> 以阻止该错误继续向上传播。</p>
<div class="warning">
<p>你可以在此钩子中修改组件的状态。因此在捕获错误时，在模板或渲染函数中有一个条件判断来绕过其它内容就很重要；不然该组件可能会进入一个无限的渲染循环。</p>
</div>
<p><strong>错误传播规则</strong></p>
<p>默认情况下，如果全局的 <code>config.errorHandler</code> 被定义，所有的错误仍会发送它，因此这些错误仍然会向单一的分析服务的地方进行汇报。</p>
<p>如果一个组件的 <code>inheritance chain</code> (继承链)或 <code>parent chain</code> (父链)中存在多个 <code>errorCaptured</code> 钩子，则它们将会被相同的错误逐个唤起。</p>
<p>如果此 <code>errorCaptured</code> 钩子自身抛出了一个错误，则这个新错误和原本被捕获的错误都会发送给全局的 <code>config.errorHandler</code>。</p>
<p>一个 <code>errorCaptured</code> 钩子能够返回 <code>false</code> 以阻止错误继续向上传播。本质上是说“这个错误已经被搞定了且应该被忽略”。它会阻止其它任何会被这个错误唤起的 <code>errorCaptured</code> 钩子和全局的 <code>config.errorHandler</code>。</p>
<h2>选项 / 资源</h2>
<h3>directives</h3>
<p>类型：<code>Object</code></p>
<p>详细：包含 <code>Vue</code> 实例可用指令的哈希表。</p>
<h3>filters</h3>
<p>类型：<code>Object</code></p>
<p>详细：包含 <code>Vue</code> 实例可用过滤器的哈希表。</p>
<h3>components</h3>
<p>类型：<code>Object</code></p>
<p>详细：包含 <code>Vue</code>实例可用组件的哈希表。</p>
<h2>选项/组合</h2>
<h3>parent</h3>
<p>类型：<code>Vue instance</code></p>
<p>详细：</p>
<p>指定已创建的实例之父实例，在两者之间建立父子关系。子实例可以用 <code>this.$parent</code> 访问父实例，子实例被推入父实例的 <code>$children</code> 数组中。</p>
<p>节制地使用 <code>$parent</code> 和 <code>$children</code> - 它们的主要目的是作为访问组件的应急方法。更推荐用 <code>props</code> 和 <code>events</code> 实现父子组件通信</p>
<h3>mixins</h3>
<p>类型：<code>Array&lt;Object&gt;</code></p>
<p>详细：</p>
<p><code>mixins</code> 选项接收一个混入对象的数组。这些混入对象可以像正常的实例对象一样包含实例选项，这些选项将会被合并到最终的选项中，使用的是和 <code>Vue.extend()</code> 一样的选项合并逻辑。也就是说，如果你的混入包含一个 <code>created</code> 钩子，而创建组件本身也有一个，那么两个函数都会被调用。</p>
<p><code>Mixin</code> 钩子按照传入顺序依次调用，并在调用组件自身的钩子之前被调用。</p>
<p>示例：</p>
<pre class="hljs"><code><span class="hljs-keyword">var</span> mixin = {
  <span class="hljs-attr">created</span>: <span class="hljs-keyword">function</span> (<span class="hljs-params"></span>) { <span class="hljs-variable language_">console</span>.<span class="hljs-title function_">log</span>(<span class="hljs-number">1</span>) }
}
<span class="hljs-keyword">var</span> vm = <span class="hljs-keyword">new</span> <span class="hljs-title class_">Vue</span>({
  <span class="hljs-attr">created</span>: <span class="hljs-keyword">function</span> (<span class="hljs-params"></span>) { <span class="hljs-variable language_">console</span>.<span class="hljs-title function_">log</span>(<span class="hljs-number">2</span>) },
  <span class="hljs-attr">mixins</span>: [mixin]
})
<span class="hljs-comment">// =&gt; 1</span>
<span class="hljs-comment">// =&gt; 2</span>
</code></pre>
<h3>extends</h3>
<p>类型：<code>Object | Function</code></p>
<p>详细：</p>
<p>允许声明扩展另一个组件 (可以是一个简单的选项对象或构造函数)，而无需使用 <code>Vue.extend</code>。这主要是为了便于扩展单文件组件。</p>
<p>这和 <code>mixins</code> 类似。</p>
<p>示例：</p>
<pre class="hljs"><code><span class="hljs-keyword">var</span> <span class="hljs-title class_">Comp</span>A = { ... }

<span class="hljs-comment">// 在没有调用 `Vue.extend` 时候继承 CompA</span>
<span class="hljs-keyword">var</span> <span class="hljs-title class_">Comp</span>B = {
  <span class="hljs-attr">extends</span>: <span class="hljs-title class_">Comp</span>A,
  ...
}
</code></pre>
<h3>provide / inject</h3>
<div class="tip">
<p>2.2.0 新增</p>
</div>
<p>类型：</p>
<p><code>provide：Object | () =&gt; Object</code></p>
<p><code>inject：Array&lt;string&gt; | { [key: string]: string | Symbol | Object }</code></p>
<p>详细：</p>
<p>这对选项需要一起使用，以允许一个祖先组件向其所有子孙后代注入一个依赖，不论组件层次有多深，并在其上下游关系成立的时间里始终生效。如果你熟悉 <code>React</code>，这与 <code>React</code> 的上下文特性很相似。</p>
<p><code>provide</code> 选项应该是一个对象或返回一个对象的函数。该对象包含可注入其子孙的 <code>property</code>。在该对象中你可以使用 <code>ES2015 Symbols</code> 作为 <code>key</code>，但是只在原生支持 <code>Symbol</code> 和 <code>Reflect.ownKeys</code> 的环境下可工作。</p>
<p><code>inject</code> 选项应该是：</p>
<pre class="hljs"><code>- 一个字符串数组，或
- 一个对象，对象的 key 是本地的绑定名，value 是：
  - 在可用的注入内容中搜索用的 key (字符串或 <span class="hljs-title class_">Symbol</span>)，或
  - 一个对象，该对象的：
    - <span class="hljs-keyword">from</span> property 是在可用的注入内容中搜索用的 key (字符串或 <span class="hljs-title class_">Symbol</span>)
    - <span class="hljs-keyword">default</span> property 是降级情况下使用的 value
</code></pre>
<div class="tip">
<p>提示：provide 和 inject 绑定并不是可响应的。这是刻意为之的。然而，如果你传入了一个可监听的对象，那么其对象的 property 还是可响应的。</p>
</div>
<p>示例：</p>
<pre class="hljs"><code><span class="hljs-comment">// 父级组件提供 &#x27;foo&#x27;</span>
<span class="hljs-keyword">var</span> <span class="hljs-title class_">Provider</span> = {
  <span class="hljs-attr">provide</span>: {
    <span class="hljs-attr">foo</span>: <span class="hljs-string">&#x27;bar&#x27;</span>
  },
  <span class="hljs-comment">// ...</span>
}

<span class="hljs-comment">// 子组件注入 &#x27;foo&#x27;</span>
<span class="hljs-keyword">var</span> <span class="hljs-title class_">Child</span> = {
  <span class="hljs-attr">inject</span>: [<span class="hljs-string">&#x27;foo&#x27;</span>],
  created () {
    <span class="hljs-variable language_">console</span>.<span class="hljs-title function_">log</span>(<span class="hljs-variable language_">this</span>.<span class="hljs-property">foo</span>) <span class="hljs-comment">// =&gt; &quot;bar&quot;</span>
  }
  <span class="hljs-comment">// ...</span>
}
</code></pre>
<p>利用 <code>ES2015 Symbols</code>、函数 <code>provide</code> 和对象 <code>inject：</code></p>
<pre class="hljs"><code><span class="hljs-keyword">const</span> s = <span class="hljs-title class_">Symbol</span>()

<span class="hljs-keyword">const</span> <span class="hljs-title class_">Provider</span> = {
  provide () {
    <span class="hljs-keyword">return</span> {
      [s]: <span class="hljs-string">&#x27;foo&#x27;</span>
    }
  }
}

<span class="hljs-keyword">const</span> <span class="hljs-title class_">Child</span> = {
  <span class="hljs-attr">inject</span>: { s },
  <span class="hljs-comment">// ...</span>
}
</code></pre>
<div class="tip">
<p>接下来 <code>2</code> 个例子只工作在 <code>Vue 2.2.1</code> 或更高版本。低于这个版本时，注入的值会在 <code>props</code> 和 <code>data</code> 初始化之后得到。</p>
</div>
<p>使用一个注入的值作为一个 <code>property</code> 的默认值：</p>
<pre class="hljs"><code><span class="hljs-keyword">const</span> <span class="hljs-title class_">Child</span> = {
  <span class="hljs-attr">inject</span>: [<span class="hljs-string">&#x27;foo&#x27;</span>],
  <span class="hljs-attr">props</span>: {
    <span class="hljs-attr">bar</span>: {
      <span class="hljs-keyword">default</span> () {
        <span class="hljs-keyword">return</span> <span class="hljs-variable language_">this</span>.<span class="hljs-property">foo</span>
      }
    }
  }
}
</code></pre>
<p>使用一个注入的值作为数据入口：</p>
<pre class="hljs"><code><span class="hljs-keyword">const</span> <span class="hljs-title class_">Child</span> = {
  <span class="hljs-attr">inject</span>: [<span class="hljs-string">&#x27;foo&#x27;</span>],
  data () {
    <span class="hljs-keyword">return</span> {
      <span class="hljs-attr">bar</span>: <span class="hljs-variable language_">this</span>.<span class="hljs-property">foo</span>
    }
  }
}
</code></pre>
<div class="tip">
<p>在 <code>2.5.0+</code> 的注入可以通过设置默认值使其变成可选项：</p>
</div>
<pre class="hljs"><code><span class="hljs-keyword">const</span> <span class="hljs-title class_">Child</span> = {
  <span class="hljs-attr">inject</span>: {
    <span class="hljs-attr">foo</span>: { <span class="hljs-attr">default</span>: <span class="hljs-string">&#x27;foo&#x27;</span> }
  }
}
</code></pre>
<p>如果它需要从一个不同名字的 <code>property</code> 注入，则使用 <code>from</code> 来表示其源 <code>property：</code></p>
<pre class="hljs"><code><span class="hljs-keyword">const</span> <span class="hljs-title class_">Child</span> = {
  <span class="hljs-attr">inject</span>: {
    <span class="hljs-attr">foo</span>: {
      <span class="hljs-attr">from</span>: <span class="hljs-string">&#x27;bar&#x27;</span>,
      <span class="hljs-attr">default</span>: <span class="hljs-string">&#x27;foo&#x27;</span>
    }
  }
}
</code></pre>
<p>与 <code>prop</code> 的默认值类似，你需要对非原始值使用一个工厂方法：</p>
<pre class="hljs"><code><span class="hljs-keyword">const</span> <span class="hljs-title class_">Child</span> = {
  <span class="hljs-attr">inject</span>: {
    <span class="hljs-attr">foo</span>: {
      <span class="hljs-attr">from</span>: <span class="hljs-string">&#x27;bar&#x27;</span>,
      <span class="hljs-attr">default</span>: <span class="hljs-function">() =&gt;</span> [<span class="hljs-number">1</span>, <span class="hljs-number">2</span>, <span class="hljs-number">3</span>]
    }
  }
}
</code></pre>
<h2>选项/其它</h2>
<h3>name</h3>
<p>类型：<code>string</code></p>
<p>限制：只有作为组件选项时起作用。</p>
<p>详细：</p>
<p>允许组件模板递归地调用自身。注意，组件在全局用 <code>Vue.component()</code> 注册时，全局 <code>ID</code> 自动作为组件的 <code>name。</code></p>
<p>指定 <code>name</code> 选项的另一个好处是便于调试。有名字的组件有更友好的警告信息。另外，当在有 <code>vue-devtools</code>，未命名组件将显示成 <code>&lt;AnonymousComponent&gt;</code>，这很没有语义。通过提供 <code>name</code> 选项，可以获得更有语义信息的组件树。</p>
<h3>delimiters</h3>
<p>类型：<code>Array&lt;string&gt;</code></p>
<p>默认值：<code>[&quot;{{&quot;, &quot;}}&quot;]</code></p>
<p>限制：这个选项只在完整构建版本中的浏览器内编译时可用。</p>
<p>详细：</p>
<p>改变纯文本插入分隔符。</p>
<p>示例：</p>
<pre class="hljs"><code><span class="hljs-keyword">new</span> <span class="hljs-title class_">Vue</span>({
  <span class="hljs-attr">delimiters</span>: [<span class="hljs-string">&#x27;${&#x27;</span>, <span class="hljs-string">&#x27;}&#x27;</span>]
})

<span class="hljs-comment">// 分隔符变成了 ES6 模板字符串的风格</span>
</code></pre>
<h3>functional</h3>
<p>类型：<code>boolean</code></p>
<p>详细：</p>
<p>使组件无状态 (没有 <code>data</code>) 和无实例 (没有 <code>this</code> 上下文)。他们用一个简单的 <code>render</code> 函数返回虚拟节点使它们渲染的代价更小。</p>
<h3>model</h3>
<div class="tip">
<p>2.2.0 新增</p>
</div>
<p>详细：</p>
<p>允许一个自定义组件在使用 <code>v-model</code> 时定制 <code>prop</code> 和 <code>event</code>。默认情况下，一个组件上的 <code>v-model</code> 会把 <code>value</code> 用作 <code>prop</code> 且把 <code>input</code> 用作 <code>event</code>，但是一些输入类型比如<strong>单选框</strong>和<strong>复选框按钮</strong>可能想使用 <code>value prop</code> 来达到不同的目的。使用 <code>model</code> 选项可以回避这些情况产生的冲突。</p>
<p>示例：</p>
<pre class="hljs"><code><span class="hljs-title class_">Vue</span>.<span class="hljs-title function_">component</span>(<span class="hljs-string">&#x27;my-checkbox&#x27;</span>, {
  <span class="hljs-attr">model</span>: {
    <span class="hljs-attr">prop</span>: <span class="hljs-string">&#x27;checked&#x27;</span>,
    <span class="hljs-attr">event</span>: <span class="hljs-string">&#x27;change&#x27;</span>
  },
  <span class="hljs-attr">props</span>: {
    <span class="hljs-comment">// this allows using the `value` prop for a different purpose</span>
    <span class="hljs-attr">value</span>: <span class="hljs-title class_">String</span>,
    <span class="hljs-comment">// use `checked` as the prop which take the place of `value`</span>
    <span class="hljs-attr">checked</span>: {
      <span class="hljs-attr">type</span>: <span class="hljs-title class_">Number</span>,
      <span class="hljs-attr">default</span>: <span class="hljs-number">0</span>
    }
  },
  <span class="hljs-comment">// ...</span>
})
</code></pre>
<pre class="hljs"><code>&lt;my-checkbox v-model=<span class="hljs-string">&quot;foo&quot;</span> value=<span class="hljs-string">&quot;some value&quot;</span>&gt;&lt;/my-checkbox&gt;
</code></pre>
<p>上述代码相当于：</p>
<pre class="hljs"><code>&lt;my-checkbox
  :checked=<span class="hljs-string">&quot;foo&quot;</span>
  @change=<span class="hljs-string">&quot;val =&gt; { foo = val }&quot;</span>
  value=<span class="hljs-string">&quot;some value&quot;</span>&gt;
&lt;/my-checkbox&gt;
</code></pre>
<h3>inheritAttrs</h3>
<div class="tip">
<p>2.4.0 新增</p>
</div>
<p>类型：<code>boolean</code></p>
<p>默认值：<code>true</code></p>
<p>详细：</p>
<p>默认情况下父作用域的不被认作 <code>props</code> 的 <code>attribute</code> 绑定 (<code>attribute bindings</code>) 将会“回退”且作为普通的 <code>HTML attribute</code> 应用在子组件的根元素上。当撰写包裹一个目标元素或另一个组件的组件时，这可能不会总是符合预期行为。通过设置 <code>inheritAttrs</code> 到 <code>false</code>，这些默认行为将会被去掉。而通过 (同样是 <code>2.4</code> 新增的) 实例 <code>property $attrs</code> 可以让这些 <code>attribute</code> 生效，且可以通过 <code>v-bind</code> 显性的绑定到非根元素上。</p>
<div class="warning">
<p>注意：这个选项不影响 <code>class</code> 和 <code>style</code> 绑定。</p>
</div>
<h3>comments</h3>
<div class="tip">
<p>2.4.0 新增</p>
</div>
<p>类型：<code>boolean</code></p>
<p>默认值：<code>false</code></p>
<p>限制：这个选项只在完整构建版本中的浏览器内编译时可用。</p>
<p>详细：</p>
<p>当设为 <code>true</code> 时，将会保留且渲染模板中的 <code>HTML</code> 注释。默认行为是舍弃它们。</p>
<h2>实例property</h2>
<h3>vm.$data</h3>
<p>类型：<code>Object</code></p>
<p>详细：</p>
<p><code>Vue</code> 实例观察的数据对象。<code>Vue</code> 实例代理了对其 <code>data</code> 对象 <code>property</code> 的访问。</p>
<h3>vm.$props</h3>
<div class="tip">
<p>2.2.0 新增</p>
</div>
<p>类型：<code>Object</code></p>
<p>详细：</p>
<p>当前组件接收到的 <code>props</code> 对象。Vue 实例代理了对其 <code>props</code> 对象 <code>property</code> 的访问。</p>
<h3>vm.$el</h3>
<p>类型：<code>Element</code></p>
<p>只读</p>
<p>详细：</p>
<p><code>Vue</code> 实例使用的根 <code>DOM</code> 元素。</p>
<h3>vm.$options</h3>
<p>类型：<code>Object</code></p>
<p>只读</p>
<p>详细：</p>
<p>用于当前 <code>Vue</code> 实例的初始化选项。需要在选项中包含自定义 <code>property</code> 时会有用处：</p>
<pre class="hljs"><code><span class="hljs-keyword">new</span> <span class="hljs-title class_">Vue</span>({
  <span class="hljs-attr">customOption</span>: <span class="hljs-string">&#x27;foo&#x27;</span>,
  <span class="hljs-attr">created</span>: <span class="hljs-keyword">function</span> (<span class="hljs-params"></span>) {
    <span class="hljs-variable language_">console</span>.<span class="hljs-title function_">log</span>(<span class="hljs-variable language_">this</span>.<span class="hljs-property">$options</span>.<span class="hljs-property">customOption</span>) <span class="hljs-comment">// =&gt; &#x27;foo&#x27;</span>
  }
})
</code></pre>
<h3>vm.$parent</h3>
<p>类型：<code>Vue instance</code></p>
<p>只读</p>
<p>详细：</p>
<p>父实例，如果当前实例有的话。</p>
<h3>vm.$root</h3>
<p>类型：<code>Vue instance</code></p>
<p>只读</p>
<p>详细：</p>
<p>当前组件树的根 <code>Vue</code> 实例。如果当前实例没有父实例，此实例将会是其自己。</p>
<h3>vm.$children</h3>
<p>类型：<code>Array&lt;Vue instance&gt;</code></p>
<p>只读</p>
<p>详细：</p>
<p>当前实例的直接子组件。需要注意 <code>$children</code> 并不保证顺序，也不是响应式的。如果你发现自己正在尝试使用 <code>$children</code> 来进行数据绑定，考虑使用一个数组配合 <code>v-for</code> 来生成子组件，并且使用 <code>Array</code> 作为真正的来源。</p>
<h3>vm.$slots</h3>
<p>类型：<code>{ [name: string]: ?Array&lt;VNode&gt; }</code></p>
<p>只读</p>
<p>响应性：否</p>
<p>详细：</p>
<p>用来访问被插槽分发的内容。每个具名插槽有其相应的 <code>property</code> (例如：<code>v-slot:foo</code> 中的内容将会在 <code>vm.$slots.foo</code> 中被找到)。<code>default property</code> 包括了所有没有被包含在具名插槽中的节点，或 <code>v-slot:default</code> 的内容。</p>
<p>请注意插槽<strong>不是</strong>响应性的。如果你需要一个组件可以在被传入的数据发生变化时重渲染，我们建议改变策略，依赖诸如 <code>props</code> 或 <code>data</code> 等响应性实例选项。</p>
<p>注意：<code>v-slot:foo</code> 在 <code>2.6</code> 以上的版本才支持。对于之前的版本，你可以使用废弃了的语法。</p>
<p>在使用<strong>渲染函数</strong>书写一个组件时，访问 <code>vm.$slots</code> 最有帮助。</p>
<p>示例：</p>
<pre class="hljs"><code>&lt;blog-post&gt;
  <span class="language-xml"><span class="hljs-tag">&lt;<span class="hljs-name">template</span> <span class="hljs-attr">v-slot:header</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">h1</span>&gt;</span>About Me<span class="hljs-tag">&lt;/<span class="hljs-name">h1</span>&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span></span>

  <span class="language-xml"><span class="hljs-tag">&lt;<span class="hljs-name">p</span>&gt;</span>Here&#x27;s some page content, which will be included in vm.$slots.default, because it&#x27;s not inside a named slot.<span class="hljs-tag">&lt;/<span class="hljs-name">p</span>&gt;</span></span>

  <span class="language-xml"><span class="hljs-tag">&lt;<span class="hljs-name">template</span> <span class="hljs-attr">v-slot:footer</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">p</span>&gt;</span>Copyright 2016 Evan You<span class="hljs-tag">&lt;/<span class="hljs-name">p</span>&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span></span>

  <span class="language-xml"><span class="hljs-tag">&lt;<span class="hljs-name">p</span>&gt;</span>If I have some content down here, it will also be included in vm.$slots.default.<span class="hljs-tag">&lt;/<span class="hljs-name">p</span>&gt;</span></span>.
&lt;/blog-post&gt;
</code></pre>
<pre class="hljs"><code><span class="hljs-title class_">Vue</span>.<span class="hljs-title function_">component</span>(<span class="hljs-string">&#x27;blog-post&#x27;</span>, {
  <span class="hljs-attr">render</span>: <span class="hljs-keyword">function</span> (<span class="hljs-params">createElement</span>) {
    <span class="hljs-keyword">var</span> header = <span class="hljs-variable language_">this</span>.<span class="hljs-property">$slots</span>.<span class="hljs-property">header</span>
    <span class="hljs-keyword">var</span> body   = <span class="hljs-variable language_">this</span>.<span class="hljs-property">$slots</span>.<span class="hljs-property">default</span>
    <span class="hljs-keyword">var</span> footer = <span class="hljs-variable language_">this</span>.<span class="hljs-property">$slots</span>.<span class="hljs-property">footer</span>
    <span class="hljs-keyword">return</span> <span class="hljs-title function_">createElement</span>(<span class="hljs-string">&#x27;div&#x27;</span>, [
      <span class="hljs-title function_">createElement</span>(<span class="hljs-string">&#x27;header&#x27;</span>, header),
      <span class="hljs-title function_">createElement</span>(<span class="hljs-string">&#x27;main&#x27;</span>, body),
      <span class="hljs-title function_">createElement</span>(<span class="hljs-string">&#x27;footer&#x27;</span>, footer)
    ])
  }
})
</code></pre>
<h3>vm.$scopedSlots</h3>
<div class="tip">
<p>2.1.0 新增</p>
</div>
<p>类型：<code>{ [name: string]: props =&gt; Array&lt;VNode&gt; | undefined }</code></p>
<p>只读</p>
<p>详细：</p>
<p>用来访问作用域插槽。对于包括 默认 <code>slot</code> 在内的每一个插槽，该对象都包含一个返回相应 <code>VNode</code> 的函数。</p>
<p><code>vm.$scopedSlots</code> 在使用渲染函数开发一个组件时特别有用。</p>
<p>注意：从 <code>2.6.0</code> 开始，这个 <code>property</code> 有两个变化：</p>
<p>作用域插槽函数现在保证返回一个 <code>VNode</code> 数组，除非在返回值无效的情况下返回 <code>undefined。</code></p>
<p>所有的 <code>$slots</code> 现在都会作为函数暴露在 <code>$scopedSlots</code> 中。如果你在使用渲染函数，不论当前插槽是否带有作用域，我们都推荐始终通过 <code>$scopedSlots</code> 访问它们。这不仅仅使得在未来添加作用域变得简单，也可以让你最终轻松迁移到所有插槽都是函数的 <code>Vue 3</code>。</p>
<h3>vm.$refs</h3>
<p>类型：<code>Object</code></p>
<p>只读</p>
<p>详细：</p>
<p>一个对象，持有注册过 <code>ref attribute</code> 的所有 <code>DOM</code> 元素和组件实例。</p>
<h3>vm.$isServer</h3>
<p>类型：<code>boolean</code></p>
<p>只读</p>
<p>详细：</p>
<p>当前 <code>Vue</code> 实例是否运行于服务器。</p>
<h3>vm.$attrs</h3>
<div class="tip">
<p>2.4.0 新增</p>
</div>
<p>类型：<code>{ [key: string]: string }</code></p>
<p>只读</p>
<p>详细：</p>
<p>包含了父作用域中不作为 <code>prop</code> 被识别 (且获取) 的 <code>attribute</code> 绑定 (<code>class</code> 和 <code>style</code> 除外)。当一个组件没有声明任何 <code>prop</code> 时，这里会包含所有父作用域的绑定 (<code>class</code> 和 <code>style</code> 除外)，并且可以通过 <code>v-bind=&quot;$attrs&quot;</code> 传入内部组件——在创建高级别的组件时非常有用。</p>
<h3>vm.$listeners</h3>
<div class="tip">
<p>2.4.0 新增</p>
</div>
<p>类型：<code>{ [key: string]: Function | Array&lt;Function&gt; }</code></p>
<p>只读</p>
<p>详细：</p>
<p>包含了父作用域中的 (不含 <code>.native</code> 修饰器的) <code>v-on</code> 事件监听器。它可以通过 <code>v-on=&quot;$listeners&quot;</code> 传入内部组件——在创建更高层次的组件时非常有用。</p>
<h2>实例方法/数据</h2>
<h3>vm.$watch(wepOrFn, callback, [options])</h3>
<p>参数：</p>
<pre class="hljs"><code>{string | <span class="hljs-title class_">Function</span>} expOrFn
{<span class="hljs-title class_">Function</span> | <span class="hljs-title class_">Object</span>} callback
{<span class="hljs-title class_">Object</span>} [options]
  {boolean} deep
  {boolean} immediate
</code></pre>
<p>返回值：<code>{Function} unwatch</code></p>
<p>用法：</p>
<p>观察 <code>Vue</code> 实例上的一个表达式或者一个函数计算结果的变化。回调函数得到的参数为新值和旧值。表达式只接受简单的键路径。对于更复杂的表达式，用一个函数取代。</p>
<div class="warning">
<p>注意：在变更 (不是替换) 对象或数组时，旧值将与新值相同，因为它们的引用指向同一个对象/数组。Vue 不会保留变更之前值的副本。</p>
</div>
<p>示例：</p>
<pre class="hljs"><code><span class="hljs-comment">// 键路径</span>
vm.$watch(<span class="hljs-string">&#x27;a.b.c&#x27;</span>, <span class="hljs-keyword">function</span> (<span class="hljs-params">newVal, oldVal</span>) {
  <span class="hljs-comment">// 做点什么</span>
})

<span class="hljs-comment">// 函数</span>
vm.$watch(
  <span class="hljs-keyword">function</span> (<span class="hljs-params"></span>) {
    <span class="hljs-comment">// 表达式 `this.a + this.b` 每次得出一个不同的结果时</span>
    <span class="hljs-comment">// 处理函数都会被调用。</span>
    <span class="hljs-comment">// 这就像监听一个未被定义的计算属性</span>
    <span class="hljs-keyword">return</span> <span class="hljs-variable language_">this</span>.<span class="hljs-property">a</span> + <span class="hljs-variable language_">this</span>.<span class="hljs-property">b</span>
  },
  <span class="hljs-keyword">function</span> (<span class="hljs-params">newVal, oldVal</span>) {
    <span class="hljs-comment">// 做点什么</span>
  }
)
</code></pre>
<p><code>vm.$watch</code> 返回一个取消观察函数，用来停止触发回调：</p>
<pre class="hljs"><code><span class="hljs-keyword">var</span> unwatch = vm.$watch(<span class="hljs-string">&#x27;a&#x27;</span>, cb)
<span class="hljs-comment">// 之后取消观察</span>
<span class="hljs-title function_">unwatch</span>()
</code></pre>
<p>选项：<code>deep</code></p>
<p>为了发现对象内部值的变化，可以在选项参数中指定 <code>deep: true</code>。注意监听<strong>数组的变更</strong>不需要这么做。</p>
<pre class="hljs"><code>vm.$watch(<span class="hljs-string">&#x27;someObject&#x27;</span>, callback, {
  <span class="hljs-attr">deep</span>: <span class="hljs-literal">true</span>
})
vm.<span class="hljs-property">someObject</span>.<span class="hljs-property">nestedValue</span> = <span class="hljs-number">123</span>
<span class="hljs-comment">// callback is fired</span>
</code></pre>
<p>选项：<code>immediate</code></p>
<p>在选项参数中指定 <code>immediate: true</code> 将立即以表达式的当前值触发回调：</p>
<pre class="hljs"><code>vm.$watch(<span class="hljs-string">&#x27;a&#x27;</span>, callback, {
  <span class="hljs-attr">immediate</span>: <span class="hljs-literal">true</span>
})
<span class="hljs-comment">// 立即以 `a` 的当前值触发回调</span>
</code></pre>
<p>注意在带有 <code>immediate</code> 选项时，你不能在第一次回调时取消侦听给定的property</p>
<pre class="hljs"><code><span class="hljs-comment">// 这会导致报错</span>
<span class="hljs-keyword">var</span> unwatch = vm.$watch(
  <span class="hljs-string">&#x27;value&#x27;</span>,
  <span class="hljs-keyword">function</span>(<span class="hljs-params"></span>) {
    <span class="hljs-title function_">doSomething</span>()
    <span class="hljs-title function_">unwatch</span>()
  },{
    <span class="hljs-attr">immediate</span>: <span class="hljs-literal">true</span>
  }
)
</code></pre>
<p>如果你仍然希望在回调内部调用一个取消侦听的函数，你应该先检查其函数的可用性：</p>
<pre class="hljs"><code><span class="hljs-keyword">var</span> unwatch = vm.$watch(
  <span class="hljs-string">&#x27;value&#x27;</span>,
  <span class="hljs-keyword">function</span> (<span class="hljs-params"></span>) {
    <span class="hljs-title function_">doSomething</span>()
    <span class="hljs-keyword">if</span> (unwatch) {
      <span class="hljs-title function_">unwatch</span>()
    }
  },
  { <span class="hljs-attr">immediate</span>: <span class="hljs-literal">true</span> }
)
</code></pre>
<h3>vm.$set(target, propertyName/index, value)</h3>
<p>参数：</p>
<p><code>{Object | Array} target</code></p>
<p><code>{string | number} propertyName/index</code></p>
<p><code>{any} value}</code></p>
<p>返回值：设置的值</p>
<p>用法：这是全局<code>Vue.set</code>的别名。</p>
<h3>vm.$delete(target, propertyName/index)</h3>
<p>参数：</p>
<p><code>{Object | Array} target</code></p>
<p><code>{string | number} propertyName/index</code></p>
<p>用法：这是全局 <code>Vue.delete</code> 的别名</p>
<h2>实例方法/事件</h2>
<h3>vm.$on(event, callback)</h3>
<p>参数：</p>
<p><code>{string | Array&lt;string&gt;} event</code></p>
<p><code>{Function} callback</code></p>
<p>用法：监听当前实例上的自定义事件。事件可以由 <code>vm.$emit</code> 触发。 回调函数会接收所有传入事件触发函数的额外参数。</p>
<p>示例：</p>
<pre class="hljs"><code>vm.$on(<span class="hljs-string">&#x27;test&#x27;</span>, <span class="hljs-keyword">function</span>(<span class="hljs-params">msg</span>){
  <span class="hljs-variable language_">console</span>.<span class="hljs-title function_">log</span>(msg)
})

vm.$emit(<span class="hljs-string">&#x27;test&#x27;</span>, <span class="hljs-string">&#x27;hi)
// =&gt; &#x27;</span>hi<span class="hljs-string">&#x27;
</span></code></pre>
<h3>vm.$once(event, callback)</h3>
<p>参数：</p>
<p><code>{string} event</code></p>
<p><code>{Function} callback</code></p>
<p>用法： 监听一个自定义事件，但是只触发一次。一旦触发之后，监听器就会被移除。</p>
<h3>vm.$off( [event, callback] )</h3>
<p>参数：</p>
<p><code>{string | Array&lt;string&gt;} event (只在 2.2.2+ 支持数组)</code></p>
<p><code>{Function} [callback]</code></p>
<p>用法：</p>
<p>移除自定义事件监听器。</p>
<p>如果没有提供参数，则移除所有的事件监听器；</p>
<p>如果只提供了事件，则移除该事件所有的监听器；</p>
<p>如果同时提供了事件与回调，则只移除这个回调的监听器。</p>
<h3>vm.$emit( eventName, […args] )</h3>
<p>参数：</p>
<p><code>{string} eventName</code></p>
<p><code>[...args]</code></p>
<p>触发当前实例上的事件。附加参数都会传给监听器回调。</p>
<p>示例：</p>
<p>只配合一个事件名使用 <code>$emit</code>：</p>
<pre class="hljs"><code><span class="hljs-title class_">Vue</span>.<span class="hljs-title function_">component</span>(<span class="hljs-string">&#x27;welcome-button&#x27;</span>, {
  <span class="hljs-attr">template</span>: <span class="hljs-string">`
    &lt;button v-on:click=&quot;$emit(&#x27;welcome&#x27;)&quot;&gt;
      Click me to be welcomed
    &lt;/button&gt;
  `</span>
})
</code></pre>
<pre class="hljs"><code>&lt;div id=<span class="hljs-string">&quot;emit-example-simple&quot;</span>&gt;
  <span class="language-xml"><span class="hljs-tag">&lt;<span class="hljs-name">welcome-button</span> <span class="hljs-attr">v-on:welcome</span>=<span class="hljs-string">&quot;sayHi&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">welcome-button</span>&gt;</span></span>
&lt;/div&gt;
</code></pre>
<pre class="hljs"><code><span class="hljs-keyword">new</span> <span class="hljs-title class_">Vue</span>({
  <span class="hljs-attr">el</span>: <span class="hljs-string">&#x27;#emit-example-simple&#x27;</span>,
  <span class="hljs-attr">methods</span>: {
    <span class="hljs-attr">sayHi</span>: <span class="hljs-keyword">function</span> (<span class="hljs-params"></span>) {
      <span class="hljs-title function_">alert</span>(<span class="hljs-string">&#x27;Hi!&#x27;</span>)
    }
  }
})
</code></pre>
<p>配合额外的参数使用 <code>$emit</code>：</p>
<pre class="hljs"><code><span class="hljs-title class_">Vue</span>.<span class="hljs-title function_">component</span>(<span class="hljs-string">&#x27;magic-eight-ball&#x27;</span>, {
  <span class="hljs-attr">data</span>: <span class="hljs-keyword">function</span> (<span class="hljs-params"></span>) {
    <span class="hljs-keyword">return</span> {
      <span class="hljs-attr">possibleAdvice</span>: [<span class="hljs-string">&#x27;Yes&#x27;</span>, <span class="hljs-string">&#x27;No&#x27;</span>, <span class="hljs-string">&#x27;Maybe&#x27;</span>]
    }
  },
  <span class="hljs-attr">methods</span>: {
    <span class="hljs-attr">giveAdvice</span>: <span class="hljs-keyword">function</span> (<span class="hljs-params"></span>) {
      <span class="hljs-keyword">var</span> randomAdviceIndex = <span class="hljs-title class_">Math</span>.<span class="hljs-title function_">floor</span>(<span class="hljs-title class_">Math</span>.<span class="hljs-title function_">random</span>() * <span class="hljs-variable language_">this</span>.<span class="hljs-property">possibleAdvice</span>.<span class="hljs-property">length</span>)
      <span class="hljs-variable language_">this</span>.$emit(<span class="hljs-string">&#x27;give-advice&#x27;</span>, <span class="hljs-variable language_">this</span>.<span class="hljs-property">possibleAdvice</span>[randomAdviceIndex])
    }
  },
  <span class="hljs-attr">template</span>: <span class="hljs-string">`
    &lt;button v-on:click=&quot;giveAdvice&quot;&gt;
      Click me for advice
    &lt;/button&gt;
  `</span>
})
</code></pre>
<pre class="hljs"><code>&lt;div id=<span class="hljs-string">&quot;emit-example-argument&quot;</span>&gt;
  <span class="language-xml"><span class="hljs-tag">&lt;<span class="hljs-name">magic-eight-ball</span> <span class="hljs-attr">v-on:give-advice</span>=<span class="hljs-string">&quot;showAdvice&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">magic-eight-ball</span>&gt;</span></span>
&lt;/div&gt;
</code></pre>
<pre class="hljs"><code><span class="hljs-keyword">new</span> <span class="hljs-title class_">Vue</span>({
  <span class="hljs-attr">el</span>: <span class="hljs-string">&#x27;#emit-example-argument&#x27;</span>,
  <span class="hljs-attr">methods</span>: {
    <span class="hljs-attr">showAdvice</span>: <span class="hljs-keyword">function</span> (<span class="hljs-params">advice</span>) {
      <span class="hljs-title function_">alert</span>(advice)
    }
  }
})
</code></pre>
<h2>实例方法/生命周期</h2>
<h3>vm.$mount( [elementOrSelector] )</h3>
<p>参数：</p>
<p><code>{Element | string} [elementOrSelector]</code></p>
<p><code>{boolean} [hydrating]</code></p>
<p>返回值：<code>vm</code> - 实例自身</p>
<p>用法：</p>
<p>如果 <code>Vue</code> 实例在实例化时没有收到 <code>el</code> 选项，则它处于“未挂载”状态，没有关联的 <code>DOM</code> 元素。可以使用 <code>vm.$mount()</code> 手动地挂载一个未挂载的实例。</p>
<p>如果没有提供 <code>elementOrSelector</code> 参数，模板将被渲染为文档之外的的元素，并且你必须使用原生 <code>DOM API</code> 把它插入文档中。</p>
<p>这个方法返回实例自身，因而可以链式调用其它实例方法。</p>
<p>示例：</p>
<pre class="hljs"><code><span class="hljs-keyword">var</span> <span class="hljs-title class_">MyComponent</span> = <span class="hljs-title class_">Vue</span>.<span class="hljs-title function_">extend</span>({
  <span class="hljs-attr">template</span>: <span class="hljs-string">&#x27;&lt;div&gt;Hello!&lt;/div&gt;&#x27;</span>
})

<span class="hljs-comment">// 创建并挂载到 #app (会替换 #app)</span>
<span class="hljs-keyword">new</span> <span class="hljs-title class_">MyComponent</span>().$mount(<span class="hljs-string">&#x27;#app&#x27;</span>)

<span class="hljs-comment">// 同上</span>
<span class="hljs-keyword">new</span> <span class="hljs-title class_">MyComponent</span>({ <span class="hljs-attr">el</span>: <span class="hljs-string">&#x27;#app&#x27;</span> })

<span class="hljs-comment">// 或者，在文档之外渲染并且随后挂载</span>
<span class="hljs-keyword">var</span> component = <span class="hljs-keyword">new</span> <span class="hljs-title class_">MyComponent</span>().$mount()
<span class="hljs-variable language_">document</span>.<span class="hljs-title function_">getElementById</span>(<span class="hljs-string">&#x27;app&#x27;</span>).<span class="hljs-title function_">appendChild</span>(component.<span class="hljs-property">$el</span>)
</code></pre>
<h3>vm.$forceUpdate()</h3>
<p>示例：</p>
<p>迫使 <code>Vue</code> 实例重新渲染。注意它仅仅影响实例本身和插入插槽内容的子组件，而不是所有子组件。</p>
<h3>vm.$nextTick( [callback] )</h3>
<p>参数：</p>
<p><code>{Function} [callback]</code></p>
<p>用法：</p>
<p>将回调延迟到下次 <code>DOM</code> 更新循环之后执行。在修改数据之后立即使用它，然后等待 <code>DOM</code> 更新。它跟全局方法 <code>Vue.nextTick</code> 一样，不同的是回调的 <code>this</code> 自动绑定到调用它的实例上。</p>
<div class="tip">
<p>2.1.0 起新增：如果没有提供回调且在支持 <code>Promise</code> 的环境中，则返回一个 <code>Promise</code>。请注意 <code>Vue</code> 不自带 <code>Promise</code> 的 <code>polyfill</code>，所以如果你的目标浏览器不是原生支持 <code>Promise</code> (<code>IE：你们都看我干嘛</code>)，你得自行 <code>polyfill。</code></p>
</div>
<p>示例：</p>
<pre class="hljs"><code><span class="hljs-keyword">new</span> <span class="hljs-title class_">Vue</span>({
  <span class="hljs-comment">// ...</span>
  <span class="hljs-attr">methods</span>: {
    <span class="hljs-comment">// ...</span>
    <span class="hljs-attr">example</span>: <span class="hljs-keyword">function</span> (<span class="hljs-params"></span>) {
      <span class="hljs-comment">// 修改数据</span>
      <span class="hljs-variable language_">this</span>.<span class="hljs-property">message</span> = <span class="hljs-string">&#x27;changed&#x27;</span>
      <span class="hljs-comment">// DOM 还没有更新</span>
      <span class="hljs-variable language_">this</span>.$nextTick(<span class="hljs-keyword">function</span> (<span class="hljs-params"></span>) {
        <span class="hljs-comment">// DOM 现在更新了</span>
        <span class="hljs-comment">// `this` 绑定到当前实例</span>
        <span class="hljs-variable language_">this</span>.<span class="hljs-title function_">doSomethingElse</span>()
      })
    }
  }
})
</code></pre>
<h3>vm.$destroy()</h3>
<p>用法：</p>
<p>完全销毁一个实例。清理它与其它实例的连接，解绑它的全部指令及事件监听器。</p>
<p>触发 <code>beforeDestroy</code> 和 <code>destroyed</code> 的钩子。</p>
<div class="warning">
<p>在大多数场景中你不应该调用这个方法。最好使用 v-if 和 v-for 指令以数据驱动的方式控制子组件的生命周期。</p>
</div>
<h2>指令</h2>
<h3>v-text</h3>
<p>预期：<code>string</code></p>
<p>详细：</p>
<p>更新元素的 <code>textContent</code>。如果要更新部分的 <code>textContent</code>，需要使用 <code>{{Mustache}}</code> 插值。</p>
<p>示例：</p>
<pre class="hljs"><code><span class="hljs-tag">&lt;<span class="hljs-name">span</span> <span class="hljs-attr">v-text</span>=<span class="hljs-string">&quot;msg&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">span</span>&gt;</span>
<span class="hljs-comment">&lt;!-- 和下面的一样 --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">span</span>&gt;</span>{{data_56109acb}}<span class="hljs-tag">&lt;/<span class="hljs-name">span</span>&gt;</span>
</code></pre>
<h3>v-html</h3>
<p>预期：<code>string</code></p>
<p>详细：</p>
<p>更新元素的 <code>innerHTML</code>。注意：内容按普通 <code>HTML</code> 插入 - 不会作为 <code>Vue</code> 模板进行编译。如果试图使用 <code>v-html</code> 组合模板，可以重新考虑是否通过使用组件来替代。</p>
<p>在网站上动态渲染任意 <code>HTML</code> 是非常危险的，因为容易导致 <code>XSS</code> 攻击。只在可信内容上使用 <code>v-html</code>，永不用在用户提交的内容上。</p>
<p>在单文件组件里，<code>scoped</code> 的样式不会应用在 <code>v-html</code> 内部，因为那部分 <code>HTML</code> 没有被 <code>Vue</code> 的模板编译器处理。如果你希望针对 <code>v-html</code> 的内容设置带作用域的 <code>CSS</code>，你可以替换为 <code>CSS Modules</code> 或用一个额外的全局 <code>&lt;style&gt;</code> 元素手动设置类似 <code>BEM</code> 的作用域策略。</p>
<p>示例：</p>
<pre class="hljs"><code><span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">v-html</span>=<span class="hljs-string">&quot;html&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
</code></pre>
<h3>v-show</h3>
<p>预期：<code>any</code></p>
<p>用法：</p>
<p>根据表达式之真假值，切换元素的 <code>display CSS property</code>。</p>
<p>当条件变化时该指令触发过渡效果。</p>
<h3>v-if</h3>
<p>预期：<code>any</code></p>
<p>用法：</p>
<p>根据表达式的值的 <code>truthiness</code> 来有条件地渲染元素。在切换时元素及它的数据绑定 / 组件被销毁并重建。如果元素是 <code>&lt;template&gt;</code>，将提出它的内容作为条件块。</p>
<p>当条件变化时该指令触发过渡效果。</p>
<div class="warning">
<p>当和 <code>v-if</code> 一起使用时，<code>v-for</code> 的优先级比 <code>v-if</code> 更高。</p>
</div>
<h3>v-else</h3>
<p>不需要表达式</p>
<p>限制：前一兄弟元素必须有 <code>v-if</code> 或 <code>v-else-if</code>。</p>
<p>用法：</p>
<p>为 <code>v-if</code> 或者 <code>v-else-if</code> 添加<code>“else 块”</code>。</p>
<pre class="hljs"><code><span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">v-if</span>=<span class="hljs-string">&quot;Math.random() &gt; 0.5&quot;</span>&gt;</span>
  Now you see me
<span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">v-else</span>&gt;</span>
  Now you don&#x27;t
<span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
</code></pre>
<h3>v-else-if</h3>
<div class="tip">
<p>2.1.0 新增</p>
</div>
<p>类型：<code>any</code></p>
<p>限制：前一兄弟元素必须有 <code>v-if</code> 或 <code>v-else-if</code>。</p>
<p>用法：</p>
<p>表示 <code>v-if</code> 的<code>“else if 块”</code>。可以链式调用。</p>
<pre class="hljs"><code><span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">v-if</span>=<span class="hljs-string">&quot;type === &#x27;A&#x27;&quot;</span>&gt;</span>
  A
<span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">v-else-if</span>=<span class="hljs-string">&quot;type === &#x27;B&#x27;&quot;</span>&gt;</span>
  B
<span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">v-else-if</span>=<span class="hljs-string">&quot;type === &#x27;C&#x27;&quot;</span>&gt;</span>
  C
<span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">v-else</span>&gt;</span>
  Not A/B/C
<span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
</code></pre>
<h3>v-for</h3>
<p>预期：<code>Array | Object | number | string | Iterable (2.6 新增)</code></p>
<p>用法：</p>
<p>基于源数据多次渲染元素或模板块。此指令之值，必须使用特定语法 <code>alias in expression</code>，为当前遍历的元素提供别名：</p>
<pre class="hljs"><code><span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">v-for</span>=<span class="hljs-string">&quot;item in items&quot;</span>&gt;</span>
  {{data_99b134bc}}
<span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
</code></pre>
<p>另外也可以为数组索引指定别名 (或者用于对象的键)：</p>
<pre class="hljs"><code><span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">v-for</span>=<span class="hljs-string">&quot;(item, index) in items&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">v-for</span>=<span class="hljs-string">&quot;(val, key) in object&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">v-for</span>=<span class="hljs-string">&quot;(val, name, index) in object&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
</code></pre>
<p><code>v-for</code> 的默认行为会尝试原地修改元素而不是移动它们。要强制其重新排序元素，你需要用特殊 <code>attribute key</code> 来提供一个排序提示：</p>
<pre class="hljs"><code><span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">v-for</span>=<span class="hljs-string">&quot;item in items&quot;</span> <span class="hljs-attr">:key</span>=<span class="hljs-string">&quot;item.id&quot;</span>&gt;</span>
  {{data_99b134bc}}
<span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
</code></pre>
<p>从 <code>2.6</code> 起，<code>v-for</code> 也可以在实现了可迭代协议的值上使用，包括原生的 <code>Map</code> 和 <code>Set</code>。不过应该注意的是 <code>Vue 2.x</code> 目前并不支持可响应的 <code>Map</code> 和 <code>Set</code> 值，所以无法自动探测变更。</p>
<p>当和 <code>v-if</code> 一起使用时，<code>v-for</code> 的优先级比 <code>v-if</code> 更高。</p>
<h3>v-on</h3>
<p>缩写：<code>@</code></p>
<p>预期：<code>Function | Inline Statement | Object</code></p>
<p>参数：<code>event</code></p>
<p>修饰符：</p>
<pre class="hljs"><code>.stop - 调用 event.stopPropagation()。
.prevent - 调用 event.preventDefault()。
.capture - 添加事件侦听器时使用 capture 模式。
.self - 只当事件是从侦听器绑定的元素本身触发时才触发回调。
.{keyCode | keyAlias} - 只当事件是从特定键触发时才触发回调。
.native - 监听组件根元素的原生事件。
.once - 只触发一次回调。
.left - (2.2.0) 只当点击鼠标左键时触发。
.right - (2.2.0) 只当点击鼠标右键时触发。
.middle - (2.2.0) 只当点击鼠标中键时触发。
.passive - (2.3.0) 以 { passive: true } 模式添加侦听器。
</code></pre>
<p>用法：</p>
<p>绑定事件监听器。事件类型由参数指定。表达式可以是一个方法的名字或一个内联语句，如果没有修饰符也可以省略。</p>
<p>用在普通元素上时，只能监听原生 <code>DOM</code> 事件。用在自定义元素组件上时，也可以监听子组件触发的自定义事件。</p>
<p>在监听原生 <code>DOM</code> 事件时，方法以事件为唯一的参数。如果使用内联语句，语句可以访问一个 <code>$event property：v-on:click=&quot;handle('ok', $event)&quot;</code>。</p>
<p>从 <code>2.4.0</code> 开始，<code>v-on</code> 同样支持不带参数绑定一个事件/监听器键值对的对象。注意当使用对象语法时，是不支持任何修饰器的。</p>
<p>示例：</p>
<pre class="hljs"><code><span class="hljs-comment">&lt;!-- 方法处理器 --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">button</span> <span class="hljs-attr">v-on:click</span>=<span class="hljs-string">&quot;doThis&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">button</span>&gt;</span>

<span class="hljs-comment">&lt;!-- 动态事件 (2.6.0+) --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">button</span> <span class="hljs-attr">v-on:</span>[<span class="hljs-attr">event</span>]=<span class="hljs-string">&quot;doThis&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">button</span>&gt;</span>

<span class="hljs-comment">&lt;!-- 内联语句 --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">button</span> <span class="hljs-attr">v-on:click</span>=<span class="hljs-string">&quot;doThat(&#x27;hello&#x27;, $event)&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">button</span>&gt;</span>

<span class="hljs-comment">&lt;!-- 缩写 --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">button</span> @<span class="hljs-attr">click</span>=<span class="hljs-string">&quot;doThis&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">button</span>&gt;</span>

<span class="hljs-comment">&lt;!-- 动态事件缩写 (2.6.0+) --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">button</span> @[<span class="hljs-attr">event</span>]=<span class="hljs-string">&quot;doThis&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">button</span>&gt;</span>

<span class="hljs-comment">&lt;!-- 停止冒泡 --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">button</span> @<span class="hljs-attr">click.stop</span>=<span class="hljs-string">&quot;doThis&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">button</span>&gt;</span>

<span class="hljs-comment">&lt;!-- 阻止默认行为 --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">button</span> @<span class="hljs-attr">click.prevent</span>=<span class="hljs-string">&quot;doThis&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">button</span>&gt;</span>

<span class="hljs-comment">&lt;!-- 阻止默认行为，没有表达式 --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">form</span> @<span class="hljs-attr">submit.prevent</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">form</span>&gt;</span>

<span class="hljs-comment">&lt;!--  串联修饰符 --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">button</span> @<span class="hljs-attr">click.stop.prevent</span>=<span class="hljs-string">&quot;doThis&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">button</span>&gt;</span>

<span class="hljs-comment">&lt;!-- 键修饰符，键别名 --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">input</span> @<span class="hljs-attr">keyup.enter</span>=<span class="hljs-string">&quot;onEnter&quot;</span>&gt;</span>

<span class="hljs-comment">&lt;!-- 键修饰符，键代码 --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">input</span> @<span class="hljs-attr">keyup.13</span>=<span class="hljs-string">&quot;onEnter&quot;</span>&gt;</span>

<span class="hljs-comment">&lt;!-- 点击回调只会触发一次 --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">button</span> <span class="hljs-attr">v-on:click.once</span>=<span class="hljs-string">&quot;doThis&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">button</span>&gt;</span>

<span class="hljs-comment">&lt;!-- 对象语法 (2.4.0+) --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">button</span> <span class="hljs-attr">v-on</span>=<span class="hljs-string">&quot;{ mousedown: doThis, mouseup: doThat }&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">button</span>&gt;</span>
</code></pre>
<p>在子组件上监听自定义事件 (当子组件触发<code>“my-event”</code>时将调用事件处理器)：</p>
<pre class="hljs"><code><span class="hljs-tag">&lt;<span class="hljs-name">my-component</span> @<span class="hljs-attr">my-event</span>=<span class="hljs-string">&quot;handleThis&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">my-component</span>&gt;</span>

<span class="hljs-comment">&lt;!-- 内联语句 --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">my-component</span> @<span class="hljs-attr">my-event</span>=<span class="hljs-string">&quot;handleThis(123, $event)&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">my-component</span>&gt;</span>

<span class="hljs-comment">&lt;!-- 组件中的原生事件 --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">my-component</span> @<span class="hljs-attr">click.native</span>=<span class="hljs-string">&quot;onClick&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">my-component</span>&gt;</span>
</code></pre>
<h3>v-bind</h3>
<p>缩写：<code>:</code></p>
<p>预期：<code>any (with argument) | Object (without argument)</code></p>
<p>参数：<code>attrOrProp (optional)</code></p>
<p>修饰符：</p>
<p>.<code>prop</code> - 作为一个 <code>DOM property</code> 绑定而不是作为 <code>attribute</code> 绑定。(差别在哪里？)
.<code>camel</code> - (2.1.0+) 将 <code>kebab-case attribute</code> 名转换为 <code>camelCase。</code>(从 2.1.0 开始支持)
.<code>sync</code> (2.3.0+) 语法糖，会扩展成一个更新父组件绑定值的 <code>v-on</code> 侦听器。</p>
<p>用法：</p>
<p>动态地绑定一个或多个 <code>attribute</code>，或一个组件 <code>prop</code> 到表达式。</p>
<p>在绑定 <code>class</code> 或 <code>style attribute</code> 时，支持其它类型的值，如数组或对象。</p>
<p>在绑定 <code>prop</code> 时，<code>prop</code> 必须在子组件中声明。可以用修饰符指定不同的绑定类型。</p>
<p>没有参数时，可以绑定到一个包含键值对的对象。注意此时 <code>class</code> 和 <code>style</code> 绑定不支持数组和对象。</p>
<p>示例：</p>
<pre class="hljs"><code><span class="hljs-comment">&lt;!-- 绑定一个 attribute --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">img</span> <span class="hljs-attr">v-bind:src</span>=<span class="hljs-string">&quot;imageSrc&quot;</span>&gt;</span>

<span class="hljs-comment">&lt;!-- 动态 attribute 名 (2.6.0+) --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">button</span> <span class="hljs-attr">v-bind:</span>[<span class="hljs-attr">key</span>]=<span class="hljs-string">&quot;value&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">button</span>&gt;</span>

<span class="hljs-comment">&lt;!-- 缩写 --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">img</span> <span class="hljs-attr">:src</span>=<span class="hljs-string">&quot;imageSrc&quot;</span>&gt;</span>

<span class="hljs-comment">&lt;!-- 动态 attribute 名缩写 (2.6.0+) --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">button</span> <span class="hljs-attr">:</span>[<span class="hljs-attr">key</span>]=<span class="hljs-string">&quot;value&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">button</span>&gt;</span>

<span class="hljs-comment">&lt;!-- 内联字符串拼接 --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">img</span> <span class="hljs-attr">:src</span>=<span class="hljs-string">&quot;&#x27;/path/to/images/&#x27; + fileName&quot;</span>&gt;</span>

<span class="hljs-comment">&lt;!-- class 绑定 --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">:class</span>=<span class="hljs-string">&quot;{ red: isRed }&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">:class</span>=<span class="hljs-string">&quot;[classA, classB]&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">:class</span>=<span class="hljs-string">&quot;[classA, { classB: isB, classC: isC }]&quot;</span>&gt;</span>

<span class="hljs-comment">&lt;!-- style 绑定 --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">:style</span>=<span class="hljs-string">&quot;{ fontSize: size + &#x27;px&#x27; }&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">:style</span>=<span class="hljs-string">&quot;[styleObjectA, styleObjectB]&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>

<span class="hljs-comment">&lt;!-- 绑定一个全是 attribute 的对象 --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">v-bind</span>=<span class="hljs-string">&quot;{ id: someProp, &#x27;other-attr&#x27;: otherProp }&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>

<span class="hljs-comment">&lt;!-- 通过 prop 修饰符绑定 DOM attribute --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">v-bind:text-content.prop</span>=<span class="hljs-string">&quot;text&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>

<span class="hljs-comment">&lt;!-- prop 绑定。“prop”必须在 my-component 中声明。--&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">my-component</span> <span class="hljs-attr">:prop</span>=<span class="hljs-string">&quot;someThing&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">my-component</span>&gt;</span>

<span class="hljs-comment">&lt;!-- 通过 $props 将父组件的 props 一起传给子组件 --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">child-component</span> <span class="hljs-attr">v-bind</span>=<span class="hljs-string">&quot;$props&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">child-component</span>&gt;</span>

<span class="hljs-comment">&lt;!-- XLink --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">svg</span>&gt;</span><span class="hljs-tag">&lt;<span class="hljs-name">a</span> <span class="hljs-attr">:xlink:special</span>=<span class="hljs-string">&quot;foo&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">a</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">svg</span>&gt;</span>
</code></pre>
<p><code>.camel</code> 修饰符允许在使用 <code>DOM</code> 模板时将 <code>v-bind property</code> 名称驼峰化，例如 <code>SVG</code> 的 <code>viewBox property</code>：</p>
<pre class="hljs"><code><span class="hljs-tag">&lt;<span class="hljs-name">svg</span> <span class="hljs-attr">:view-box.camel</span>=<span class="hljs-string">&quot;viewBox&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">svg</span>&gt;</span>
</code></pre>
<p>在使用字符串模板或通过 <code>vue-loader/vueify</code> 编译时，无需使用 <code>.camel</code>。</p>
<h3>v-model</h3>
<p>预期：随表单控件类型不同而不同。</p>
<p>限制：</p>
<pre class="hljs"><code><span class="hljs-tag">&lt;<span class="hljs-name">input</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">select</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">textarea</span>&gt;</span>
components
</code></pre>
<p>修饰符：</p>
<p>.<code>lazy</code> - 取代 <code>input</code> 监听 <code>change</code> 事件
.<code>number</code> - 输入字符串转为有效的数字
.<code>trim</code> - 输入首尾空格过滤</p>
<p>用法：</p>
<p>在表单控件或者组件上创建双向绑定。</p>
<h3>v-slot</h3>
<p>缩写：<code>#</code></p>
<p>预期：可放置在函数参数位置的 <code>JavaScript</code> 表达式 (在支持的环境下可使用解构)。可选，即只需要在为插槽传入 <code>prop</code> 的时候使用。</p>
<p>参数：插槽名 (可选，默认值是 <code>default</code>)</p>
<p>限用于</p>
<p><code>&lt;template&gt;</code></p>
<p><strong>组件</strong> (对于一个单独的带 prop 的默认插槽)</p>
<p>用法：</p>
<p>提供具名插槽或需要接收 prop 的插槽。</p>
<p>示例：</p>
<pre class="hljs"><code><span class="hljs-comment">&lt;!-- 具名插槽 --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">base-layout</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">template</span> <span class="hljs-attr">v-slot:header</span>&gt;</span>
    Header content
  <span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>

  Default slot content

  <span class="hljs-tag">&lt;<span class="hljs-name">template</span> <span class="hljs-attr">v-slot:footer</span>&gt;</span>
    Footer content
  <span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">base-layout</span>&gt;</span>

<span class="hljs-comment">&lt;!-- 接收 prop 的具名插槽 --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">infinite-scroll</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">template</span> <span class="hljs-attr">v-slot:item</span>=<span class="hljs-string">&quot;slotProps&quot;</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">class</span>=<span class="hljs-string">&quot;item&quot;</span>&gt;</span>
      {{data_5f310904}}
    <span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">infinite-scroll</span>&gt;</span>

<span class="hljs-comment">&lt;!-- 接收 prop 的默认插槽，使用了解构 --&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">mouse-position</span> <span class="hljs-attr">v-slot</span>=<span class="hljs-string">&quot;{ x, y }&quot;</span>&gt;</span>
  Mouse position: {{data_51d4c452}}, {{data_51d538b1}}
<span class="hljs-tag">&lt;/<span class="hljs-name">mouse-position</span>&gt;</span>
</code></pre>
<h3>v-pre</h3>
<p>不需要表达式</p>
<p>用法：</p>
<p>跳过这个元素和它的子元素的编译过程。可以用来显示原始 <code>Mustache</code> 标签。跳过大量没有指令的节点会加快编译。</p>
<p>示例：</p>
<pre class="hljs"><code><span class="hljs-tag">&lt;<span class="hljs-name">span</span> <span class="hljs-attr">v-pre</span>&gt;</span>{{data_4a3113b5}}<span class="hljs-tag">&lt;/<span class="hljs-name">span</span>&gt;</span>
</code></pre>
<h3>v-cloak</h3>
<p>不需要表达式</p>
<p>用法：</p>
<p>这个指令保持在元素上直到关联实例结束编译。和 <code>CSS</code> 规则如 <code>[v-cloak] { display: none }</code> 一起用时，这个指令可以隐藏未编译的 <code>Mustache</code> 标签直到实例准备完毕。</p>
<p>示例：</p>
<pre class="hljs"><code>[v-cloak] {
  display: none;
}
<span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">v-cloak</span>&gt;</span>
  \{\{message\}\}
<span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
</code></pre>
<p>不会显示，直到编译结束。</p>
<h3>v-once</h3>
<p>不需要表达式</p>
<p>详细：</p>
<p>只渲染元素和组件一次。随后的重新渲染，元素/组件及其所有的子节点将被视为静态内容并跳过。这可以用于优化更新性能。</p>
<pre class="hljs"><code>&lt;!-- 单个元素 --&gt;
<span class="language-xml"><span class="hljs-tag">&lt;<span class="hljs-name">span</span> <span class="hljs-attr">v-once</span>&gt;</span>This will never change: \{\{msg\}\}<span class="hljs-tag">&lt;/<span class="hljs-name">span</span>&gt;</span></span>
&lt;!-- 有子元素 --&gt;
<span class="language-xml"><span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">v-once</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">h1</span>&gt;</span>comment<span class="hljs-tag">&lt;/<span class="hljs-name">h1</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">p</span>&gt;</span>{{data_56109acb}}<span class="hljs-tag">&lt;/<span class="hljs-name">p</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span></span>
&lt;!-- 组件 --&gt;
<span class="language-xml"><span class="hljs-tag">&lt;<span class="hljs-name">my-component</span> <span class="hljs-attr">v-once</span> <span class="hljs-attr">:comment</span>=<span class="hljs-string">&quot;msg&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">my-component</span>&gt;</span></span>
&lt;!-- <span class="hljs-string">`v-for`</span> 指令--&gt;
<span class="language-xml"><span class="hljs-tag">&lt;<span class="hljs-name">ul</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">li</span> <span class="hljs-attr">v-for</span>=<span class="hljs-string">&quot;i in list&quot;</span> <span class="hljs-attr">v-once</span>&gt;</span>{{data_787f4c33}}<span class="hljs-tag">&lt;/<span class="hljs-name">li</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">ul</span>&gt;</span></span>
</code></pre>
<h2>特殊attribute</h2>
<h3>key</h3>
<p>预期：<code>number | string | boolean (2.4.2 新增) | symbol (2.5.12 新增)</code></p>
<p><code>key</code> 的特殊 <code>attribute</code> 主要用在 <code>Vue</code> 的虚拟 <code>DOM</code> 算法，在新旧 <code>nodes</code> 对比时辨识 <code>VNodes</code>。如果不使用 <code>key</code>，<code>Vue</code> 会使用一种最大限度减少动态元素并且尽可能的尝试就地修改/复用相同类型元素的算法。而使用 <code>key</code> 时，它会基于 key 的变化重新排列元素顺序，并且会移除 <code>key</code> 不存在的元素。</p>
<p>有相同父元素的子元素必须有独特的 <code>key</code>。重复的 <code>key</code> 会造成渲染错误。</p>
<p>最常见的用例是结合<code> v-for</code>：</p>
<pre class="hljs"><code>&lt;ul&gt;
  <span class="language-xml"><span class="hljs-tag">&lt;<span class="hljs-name">li</span> <span class="hljs-attr">v-for</span>=<span class="hljs-string">&quot;item in items&quot;</span> <span class="hljs-attr">:key</span>=<span class="hljs-string">&quot;item.id&quot;</span>&gt;</span>...<span class="hljs-tag">&lt;/<span class="hljs-name">li</span>&gt;</span></span>
&lt;/ul&gt;
</code></pre>
<p>它也可以用于强制替换元素/组件而不是重复使用它。当你遇到如下场景时它可能会很有用：</p>
<p>完整地触发组件的生命周期钩子</p>
<p>触发过渡</p>
<p>例如：</p>
<pre class="hljs"><code>&lt;transition&gt;
  <span class="language-xml"><span class="hljs-tag">&lt;<span class="hljs-name">span</span> <span class="hljs-attr">:key</span>=<span class="hljs-string">&quot;text&quot;</span>&gt;</span>{{data_68c57329}}<span class="hljs-tag">&lt;/<span class="hljs-name">span</span>&gt;</span></span>
&lt;/transition&gt;
</code></pre>
<p>当 <code>text</code> 发生改变时，<code>&lt;span&gt;</code> 总是会被替换而不是被修改，因此会触发过渡。</p>
<h3>ref</h3>
<p>预期：<code>string</code></p>
<p><code>ref</code> 被用来给元素或子组件注册引用信息。引用信息将会注册在父组件的 <code>$refs</code> 对象上。如果在普通的 <code>DOM</code> 元素上使用，引用指向的就是 <code>DOM</code> 元素；如果用在子组件上，引用就指向组件实例：</p>
<pre class="hljs"><code>&lt;!-- <span class="hljs-string">`vm.$refs.p`</span> will be the <span class="hljs-variable constant_">DOM</span> node --&gt;
<span class="language-xml"><span class="hljs-tag">&lt;<span class="hljs-name">p</span> <span class="hljs-attr">ref</span>=<span class="hljs-string">&quot;p&quot;</span>&gt;</span>hello<span class="hljs-tag">&lt;/<span class="hljs-name">p</span>&gt;</span></span>

&lt;!-- <span class="hljs-string">`vm.$refs.child`</span> will be the child component instance --&gt;
<span class="language-xml"><span class="hljs-tag">&lt;<span class="hljs-name">child-component</span> <span class="hljs-attr">ref</span>=<span class="hljs-string">&quot;child&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">child-component</span>&gt;</span></span>
</code></pre>
<p>当 <code>v-for</code> 用于元素或组件的时候，引用信息将是包含 <code>DOM</code> 节点或组件实例的数组。</p>
<p>关于 <code>ref</code> 注册时间的重要说明：因为 <code>ref</code> 本身是作为渲染结果被创建的，在初始渲染的时候你不能访问它们 - 它们还不存在！<code>$refs</code> 也不是响应式的，因此你不应该试图用它在模板中做数据绑定。</p>
<h3>is</h3>
<p>预期：<code>string | Object</code> (组件的选项对象)</p>
<p>用于<strong>动态组件</strong>且基于 <code>DOM</code> <strong>内模板的限制</strong>来工作。</p>
<p>示例：</p>
<pre class="hljs"><code>&lt;!-- 当 <span class="hljs-string">`currentView`</span> 改变时，组件也跟着改变 --&gt;
<span class="language-xml"><span class="hljs-tag">&lt;<span class="hljs-name">component</span> <span class="hljs-attr">v-bind:is</span>=<span class="hljs-string">&quot;currentView&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">component</span>&gt;</span></span>

&lt;!-- 这样做是有必要的，因为 <span class="hljs-string">`&lt;my-row&gt;`</span> 放在一个 --&gt;
&lt;!-- <span class="hljs-string">`&lt;table&gt;`</span> 内可能无效且被放置到外面 --&gt;
<span class="language-xml"><span class="hljs-tag">&lt;<span class="hljs-name">table</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">tr</span> <span class="hljs-attr">is</span>=<span class="hljs-string">&quot;my-row&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">tr</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">table</span>&gt;</span></span>
</code></pre>
<h2>内置的组件</h2>
<h3>component</h3>
<p><code>Props：</code></p>
<p><code>is - string | ComponentDefinition | ComponentConstructor</code></p>
<p><code>inline-template - boolean</code></p>
<p>用法：</p>
<p>渲染一个“元组件”为动态组件。依 <code>is</code> 的值，来决定哪个组件被渲染。</p>
<pre class="hljs"><code>&lt;!-- 动态组件由 vm 实例的 <span class="hljs-string">`componentId`</span> property 控制 --&gt;
<span class="language-xml"><span class="hljs-tag">&lt;<span class="hljs-name">component</span> <span class="hljs-attr">:is</span>=<span class="hljs-string">&quot;componentId&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">component</span>&gt;</span></span>

&lt;!-- 也能够渲染注册过的组件或 prop 传入的组件 --&gt;
<span class="language-xml"><span class="hljs-tag">&lt;<span class="hljs-name">component</span> <span class="hljs-attr">:is</span>=<span class="hljs-string">&quot;$options.components.child&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">component</span>&gt;</span></span>
</code></pre>
<h3>transition</h3>
<p><code>Props：</code></p>
<pre class="hljs"><code>name - string，用于自动生成 <span class="hljs-variable constant_">CSS</span> 过渡类名。

例如：<span class="hljs-attr">name</span>: <span class="hljs-string">&#x27;fade&#x27;</span> 将自动拓展为 .<span class="hljs-property">fade</span>-enter，.<span class="hljs-property">fade</span>-enter-active 等。默认类名为 <span class="hljs-string">&quot;v&quot;</span>

appear - boolean，是否在初始渲染时使用过渡。

默认为 <span class="hljs-literal">false</span>。

css - boolean，是否使用 <span class="hljs-variable constant_">CSS</span> 过渡类。

默认为 <span class="hljs-literal">true</span>。如果设置为 <span class="hljs-literal">false</span>，将只通过组件事件触发注册的 <span class="hljs-title class_">JavaScript</span> 钩子。

type - string，指定过渡事件类型，侦听过渡何时结束。

有效值为 <span class="hljs-string">&quot;transition&quot;</span> 和 <span class="hljs-string">&quot;animation&quot;</span>。默认 <span class="hljs-title class_">Vue</span>.<span class="hljs-property">js</span> 将自动检测出持续时间长的为过渡事件类型。

mode - string，控制离开/进入过渡的时间序列。

有效的模式有 <span class="hljs-string">&quot;out-in&quot;</span> 和 <span class="hljs-string">&quot;in-out&quot;</span>；默认同时进行。

duration - number | { <span class="hljs-attr">enter</span>: number, <span class="hljs-attr">leave</span>: number } 指定过渡的持续时间。

默认情况下，<span class="hljs-title class_">Vue</span> 会等待过渡所在根元素的第一个 transitionend 或 animationend 事件。

enter-<span class="hljs-keyword">class</span> - string
leave-<span class="hljs-keyword">class</span> - string
appear-<span class="hljs-keyword">class</span> - string
enter-to-<span class="hljs-keyword">class</span> - string
leave-to-<span class="hljs-keyword">class</span> - string
appear-to-<span class="hljs-keyword">class</span> - string
enter-active-<span class="hljs-keyword">class</span> - string
leave-active-<span class="hljs-keyword">class</span> - string
appear-active-<span class="hljs-keyword">class</span> - string
</code></pre>
<p>事件：</p>
<pre class="hljs"><code>before-enter
before-leave
before-appear
enter
leave
appear
after-enter
after-leave
after-appear
enter-cancelled
leave-cancelled (v-show only)
appear-cancelled
</code></pre>
<p>用法：</p>
<p><code>&lt;transition&gt;</code> 元素作为<strong>单个</strong>元素/组件的过渡效果。<code>&lt;transition&gt;</code> 只会把过渡效果应用到其包裹的内容上，而不会额外渲染 <code>DOM</code> 元素，也不会出现在可被检查的组件层级中。</p>
<pre class="hljs"><code>&lt;!-- 简单元素 --&gt;
<span class="language-xml"><span class="hljs-tag">&lt;<span class="hljs-name">transition</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">v-if</span>=<span class="hljs-string">&quot;ok&quot;</span>&gt;</span>toggled content<span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">transition</span>&gt;</span></span>

&lt;!-- 动态组件 --&gt;
<span class="language-xml"><span class="hljs-tag">&lt;<span class="hljs-name">transition</span> <span class="hljs-attr">name</span>=<span class="hljs-string">&quot;fade&quot;</span> <span class="hljs-attr">mode</span>=<span class="hljs-string">&quot;out-in&quot;</span> <span class="hljs-attr">appear</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">component</span> <span class="hljs-attr">:is</span>=<span class="hljs-string">&quot;view&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">component</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">transition</span>&gt;</span></span>

&lt;!-- 事件钩子 --&gt;
<span class="language-xml"><span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">id</span>=<span class="hljs-string">&quot;transition-demo&quot;</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">transition</span> @<span class="hljs-attr">after-enter</span>=<span class="hljs-string">&quot;transitionComplete&quot;</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">v-show</span>=<span class="hljs-string">&quot;ok&quot;</span>&gt;</span>toggled content<span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">transition</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span></span>
</code></pre>
<pre class="hljs"><code><span class="hljs-keyword">new</span> <span class="hljs-title class_">Vue</span>({
  ...
  <span class="hljs-attr">methods</span>: {
    <span class="hljs-attr">transitionComplete</span>: <span class="hljs-keyword">function</span> (<span class="hljs-params">el</span>) {
      <span class="hljs-comment">// 传入 &#x27;el&#x27; 这个 DOM 元素作为参数。</span>
    }
  }
  ...
}).$mount(<span class="hljs-string">&#x27;#transition-demo&#x27;</span>)
</code></pre>
<h3>transition-group</h3>
<p><code>Props：</code></p>
<p><code>tag - string</code>，默认为 <code>span</code></p>
<p><code>move-class</code> - 覆盖移动过渡期间应用的 <code>CSS</code> 类。</p>
<p>除了 <code>mode</code>，其他 <code>attribute</code> 和 <code>&lt;transition&gt;</code> 相同。</p>
<p>事件：</p>
<p>事件和 <code>&lt;transition&gt;</code> 相同。</p>
<p>用法：</p>
<p><code>&lt;transition-group&gt;</code> 元素作为多个元素/组件的过渡效果。<code>&lt;transition-group&gt;</code> 渲染一个真实的 <code>DOM</code> 元素。默认渲染 <code>&lt;span&gt;</code>，可以通过 <code>tag attribute</code> 配置哪个元素应该被渲染。</p>
<p>注意，每个 <code>&lt;transition-group</code>&gt; 的子节点必须有独立的 <code>key</code>，动画才能正常工作</p>
<p><code>&lt;transition-group&gt;</code> 支持通过 <code>CSS transform</code> 过渡移动。当一个子节点被更新，从屏幕上的位置发生变化，它会被应用一个移动中的 <code>CSS</code> 类 (通过 <code>name attribute</code> 或配置 <code>move-class attribute</code> 自动生成)。如果 <code>CSS transform property</code> 是“可过渡”<code>property</code>，当应用移动类时，将会使用 <code>FLIP</code> 技术使元素流畅地到达动画终点。</p>
<pre class="hljs"><code>&lt;transition-group tag=<span class="hljs-string">&quot;ul&quot;</span> name=<span class="hljs-string">&quot;slide&quot;</span>&gt;
  <span class="language-xml"><span class="hljs-tag">&lt;<span class="hljs-name">li</span> <span class="hljs-attr">v-for</span>=<span class="hljs-string">&quot;item in items&quot;</span> <span class="hljs-attr">:key</span>=<span class="hljs-string">&quot;item.id&quot;</span>&gt;</span>
    {{data_99b134bc}}
  <span class="hljs-tag">&lt;/<span class="hljs-name">li</span>&gt;</span></span>
&lt;/transition-group&gt;
</code></pre>
<h3>keep-alive</h3>
<p><code>Props：</code></p>
<p><code>include</code>- 字符串或正则表达式。只有名称匹配的组件会被缓存。</p>
<p><code>exclude</code>- 字符串或正则表达式。任何名称匹配的组件都不会被缓存。</p>
<p><code>max</code> - 数字。最多可以缓存多少组件实例。</p>
<p>用法：</p>
<p><code>&lt;keep-alive&gt;</code> 包裹动态组件时，会缓存不活动的组件实例，而不是销毁它们。和 <code>&lt;transition&gt;</code> 相似，<code>&lt;keep-alive&gt;</code> 是一个抽象组件：它自身不会渲染一个 <code>DOM</code> 元素，也不会出现在组件的父组件链中。</p>
<p>当组件在 <code>&lt;keep-alive&gt;</code> 内被切换，它的 <code>activated</code> 和 <code>deactivated</code> 这两个生命周期钩子函数将会被对应执行。</p>
<div class="tip">
<p>在 <code>2.2.0</code> 及其更高版本中，<code>activated</code> 和 <code>deactivated</code> 将会在 <code>&lt;keep-alive&gt;</code> 树内的所有嵌套组件中触发。</p>
</div>
<p>主要用于保留组件状态或避免重新渲染。</p>
<pre class="hljs"><code>&lt;!-- 基本 --&gt;
<span class="language-xml"><span class="hljs-tag">&lt;<span class="hljs-name">keep-alive</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">component</span> <span class="hljs-attr">:is</span>=<span class="hljs-string">&quot;view&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">component</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">keep-alive</span>&gt;</span></span>

&lt;!-- 多个条件判断的子组件 --&gt;
<span class="language-xml"><span class="hljs-tag">&lt;<span class="hljs-name">keep-alive</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">comp-a</span> <span class="hljs-attr">v-if</span>=<span class="hljs-string">&quot;a &gt; 1&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">comp-a</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">comp-b</span> <span class="hljs-attr">v-else</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">comp-b</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">keep-alive</span>&gt;</span></span>

&lt;!-- 和 <span class="hljs-string">`&lt;transition&gt;`</span> 一起使用 --&gt;
<span class="language-xml"><span class="hljs-tag">&lt;<span class="hljs-name">transition</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">keep-alive</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">component</span> <span class="hljs-attr">:is</span>=<span class="hljs-string">&quot;view&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">component</span>&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">keep-alive</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">transition</span>&gt;</span></span>
</code></pre>
<p>注意，<code>&lt;keep-alive&gt;</code> 是用在其一个直属的子组件被开关的情形。如果你在其中有 <code>v-for</code> 则不会工作。如果有上述的多个条件性的子元素，<code>&lt;keep-alive&gt;</code> 要求同时只有一个子元素被渲染。</p>
<p><code>include and exclude</code></p>
<div class="tip">
<p>2.1.0 新增</p>
</div>
<p><code>include 和 exclude prop</code> 允许组件有条件地缓存。二者都可以用逗号分隔字符串、正则表达式或一个数组来表示：</p>
<pre class="hljs"><code>&lt;!-- 逗号分隔字符串 --&gt;
<span class="language-xml"><span class="hljs-tag">&lt;<span class="hljs-name">keep-alive</span> <span class="hljs-attr">include</span>=<span class="hljs-string">&quot;a,b&quot;</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">component</span> <span class="hljs-attr">:is</span>=<span class="hljs-string">&quot;view&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">component</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">keep-alive</span>&gt;</span></span>

&lt;!-- 正则表达式 (使用 <span class="hljs-string">`v-bind`</span>) --&gt;
<span class="language-xml"><span class="hljs-tag">&lt;<span class="hljs-name">keep-alive</span> <span class="hljs-attr">:include</span>=<span class="hljs-string">&quot;/a|b/&quot;</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">component</span> <span class="hljs-attr">:is</span>=<span class="hljs-string">&quot;view&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">component</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">keep-alive</span>&gt;</span></span>

&lt;!-- 数组 (使用 <span class="hljs-string">`v-bind`</span>) --&gt;
<span class="language-xml"><span class="hljs-tag">&lt;<span class="hljs-name">keep-alive</span> <span class="hljs-attr">:include</span>=<span class="hljs-string">&quot;[&#x27;a&#x27;, &#x27;b&#x27;]&quot;</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">component</span> <span class="hljs-attr">:is</span>=<span class="hljs-string">&quot;view&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">component</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">keep-alive</span>&gt;</span></span>
</code></pre>
<p>匹配首先检查组件自身的 <code>name</code> 选项，如果 <code>name</code> 选项不可用，则匹配它的局部注册名称 (父组件 <code>components</code> 选项的键值)。匿名组件不能被匹配。</p>
<p><code>max</code></p>
<div class="tip">
<p><code>2.5.0</code> 新增</p>
</div>
<p>最多可以缓存多少组件实例。一旦这个数字达到了，在新实例被创建之前，已缓存组件中最久没有被访问的实例会被销毁掉。</p>
<pre class="hljs"><code>&lt;keep-alive :max=<span class="hljs-string">&quot;10&quot;</span>&gt;
  <span class="language-xml"><span class="hljs-tag">&lt;<span class="hljs-name">component</span> <span class="hljs-attr">:is</span>=<span class="hljs-string">&quot;view&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">component</span>&gt;</span></span>
&lt;/keep-alive&gt;
</code></pre>
<div class="warning">
<p><code>&lt;keep-alive&gt;</code> 不会在函数式组件中正常工作，因为它们没有缓存实例。</p>
</div>
<h3>slot</h3>
<p><code>Props：</code></p>
<p><code>name</code> - <code>string</code>，用于命名插槽。</p>
<p>用法：</p>
<p><code>&lt;slot&gt;</code> 元素作为组件模板之中的内容分发插槽。<code>&lt;slot&gt;</code> 元素自身将被替换。</p>
<h3>VNode 接口</h3>
<h3>服务端渲染</h3>

          </div>
        </template>
        <script>
           
           export default {
           name: 'vc-component-doc',
           components: {
            
           },
           data(){
            return {data_5dd7b6a5:"{{firstName}}",data_6f5a6bf7:"{{lastName}}",data_e4b0814c:"{{alias}}",data_2175fa29:"{{ msg }}",data_2175fa29:"{{ msg }}",data_56109acb:"{{msg}}",data_99b134bc:"{{ item.text }}",data_99b134bc:"{{ item.text }}",data_5f310904:"{{ slotProps.item.text }}",data_51d4c452:"{{ x }}",data_51d538b1:"{{ y }}",data_4a3113b5:"{{ this will not be compiled }}",data_56109acb:"{{msg}}",data_787f4c33:"{{i}}",data_68c57329:"{{ text }}",data_99b134bc:"{{ item.text }}"}
           }
         }
       </script>
       <style lang='scss'>
         
       </style>